import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import {
  useEmails,
  Email,
  useEmail,
  EmailsResponse,
  EmailThreadGroup,
  listScopeQueryFromEmailsRequest,
  emailListScopeKey,
  usePersonalFolders,
  useEmailTags,
  useSharedFolders,
  useSharedSearchFolders,
  useSharedAccounts,
  useUnreadCounts,
  useSearchFolderUnreadCounts,
  useMentionUnreadCount,
  markAllMentionsRead,
  revalidateMentionCaches,
  useAccessibleAccounts,
  useImapFoldersForAccounts,
  useEmailTemplateLibrary,
  useDraftsList,
  deleteDraftApi,
  revalidateEmailDraftCaches,
  type EmailDraftListItem,
  type AccountMailboxFolder,
  type EmailsListOptions,
  type EmailsListHookResult,
  formatFolderLoadTime,
} from './useData';
import type {
  EmailPersonalFolderTreeNode,
  SharedFolderTreeNode,
  SharedSearchFolderItem,
  SharedAccountItem,
  SearchInField,
} from './useData';
import { defaultEmailSearchInSet } from './utils/emailSearchDefaults';
import { searchFolderSentByMe } from '../search-folders/useData';
import { formatDateTime } from '../../config/appConfig';
import { moduleConfig, getToolbarIcon } from './config';
import api from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { cn } from '../../lib/utils';
import EmailCardView from './components/EmailCardView';
import EmailDetailView from './components/EmailDetailView';
import EmailAiAssistant from './components/ai-assistant/EmailAiAssistant';
import EmailFilters from './components/EmailFilters';
import EmailSidebar, { type EmailSidebarDisplayPrefs } from './components/EmailSidebar';
import ShareFolderModal from './components/ShareFolderModal';
import ShareTagModal from './components/ShareTagModal';
import ShareTemplateFolderModal from './components/ShareTemplateFolderModal';
import CreatePersonalFolderModal from './components/CreatePersonalFolderModal';
import EmailBulkProgressModal, { type EmailBulkProgressState } from './components/EmailBulkProgressModal';
import MarkFolderAsReadModal, {
  type MarkFolderAsReadTarget,
} from './components/MarkFolderAsReadModal';
import TemplatesLibraryPanel from './components/TemplatesLibraryPanel';
import OutboxList from './components/OutboxList';
import type { MarkFolderAsReadFilters } from './useData';
import { Button } from '../../components/ui';
import { AnchoredMenu } from '../../components/ui/AnchoredMenu';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import {
  composeReturnFromEmailsList,
  navigateToCompose,
  openComposeInNewTab,
  stashComposeInitialUserBody,
  type ComposeListRestoreState,
  type ComposeReturnTarget,
} from './utils/composeNavigation';
import {
  emailPartitionAtSearch,
  emailComposeThreadPath,
  composeEditAsNewQuery,
} from './utils/emailPartitionAtQuery';
import {
  buildEmailDateScopeMapKey,
  loadEmailDateScopeForKey,
  searchFolderPeriodToStored,
  saveEmailDateScopeForKey,
  type EmailDateScopeStored,
} from './utils/emailDateScopeStorage';
import {
  loadEmailSidebarSelection,
  saveEmailSidebarSelection,
  type EmailSidebarSelectionStored,
  type EmailSpecialView,
} from './utils/emailSidebarSelectionStorage';
import {
  clearEmailListReturnContext,
  loadEmailListReturnContext,
  saveEmailListReturnContext,
  type EmailListPaginationSnapshot,
} from './utils/emailListReturnStorage';
import { resolveInitialEmailListState } from './utils/emailListInitialState';
import {
  receivedDateRangeForPreset,
  receivedDatePresetLabel,
  searchFolderPeriodShortLabel,
  type ReceivedDatePreset,
} from './utils/receivedDatePresets';
import {
  type EmailListSortBy,
} from './utils/emailListSortStorage';
import { useEmailUiPreferences } from './hooks/useEmailUiPreferences';
import { QuickFilterIcon, type QuickFilterKey } from './utils/emailFilterIcons';
import { CalendarIcon, SearchIcon } from '../../components/ui/Icons';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { MEDIA_QUERIES } from '../../utils/responsive';
import { COLLAPSED_RAIL_WIDTH_PX } from '../../lib/layoutConstants';
import { TablePaginationBar } from '../../components/DataTable/TablePaginationBar';

/** Max emails that can be selected at once for bulk actions (folder-wide select-all). */
const BULK_SELECT_MAX = 1000;
const BULK_SELECT_FETCH_SIZE = 500;

function persistSidebarSelection(selection: EmailSidebarSelectionStored): void {
  saveEmailSidebarSelection(selection);
}

function isSpecialEmailView(view: EmailSpecialView | null): view is EmailSpecialView {
  return (
    view === 'drafts' ||
    view === 'archived' ||
    view === 'templates' ||
    view === 'outbox' ||
    view === 'mySent'
  );
}

function isCustomPanelSpecialView(view: EmailSpecialView | null): boolean {
  return view === 'drafts' || view === 'templates' || view === 'outbox';
}

type EmailsListMutate = (data?: EmailsResponse | ((prev: EmailsResponse | undefined) => EmailsResponse | undefined), shouldRevalidate?: boolean) => Promise<EmailsResponse | undefined>;

function flattenPersonalFolders(
  nodes: EmailPersonalFolderTreeNode[],
  depth = 0
): { id: number; name: string; depth: number }[] {
  const out: { id: number; name: string; depth: number }[] = [];
  for (const node of nodes) {
    out.push({ id: node.id, name: node.name, depth });
    if (node.children?.length) {
      out.push(...flattenPersonalFolders(node.children, depth + 1));
    }
  }
  return out;
}

function flattenSharedFolderNodes(nodes: SharedFolderTreeNode[]): { shareId: number; name: string }[] {
  const out: { shareId: number; name: string }[] = [];
  for (const n of nodes) {
    out.push({ shareId: n.shareId, name: n.name });
    if (n.children?.length) out.push(...flattenSharedFolderNodes(n.children));
  }
  return out;
}

type EmailFilterChipTheme = {
  border: string;
  surface: string;
  text: string;
  textMuted: string;
  interactiveHover: string;
};

const STATUS_QUICK_FILTER_KEYS = ['statusOpen', 'statusWorking', 'statusClosed'] as const;

const STATUS_QUICK_FILTER_TO_VALUE: Record<(typeof STATUS_QUICK_FILTER_KEYS)[number], string> = {
  statusOpen: 'open',
  statusWorking: 'working',
  statusClosed: 'closed',
};

const WORKFLOW_STATUS_TO_QUICK_FILTER: Record<string, (typeof STATUS_QUICK_FILTER_KEYS)[number]> = {
  open: 'statusOpen',
  working: 'statusWorking',
  closed: 'statusClosed',
};

const QUICK_FILTER_CHIP_LABELS: Record<string, string> = {
  unread: 'Unread',
  starred: 'Starred',
  assignedToMe: 'Assigned to me',
  mentionUnreadOnly: 'Unread mentions',
  statusOpen: 'Status: Open',
  statusWorking: 'Status: In Progress',
  statusClosed: 'Status: Closed',
  attachments: 'Attachments',
  linked: 'Linked',
  archived: 'Archived',
};

function FilterChip({
  label,
  onRemove,
  colors,
  icon,
}: {
  label: string;
  onRemove: () => void;
  colors: EmailFilterChipTheme;
  icon?: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 rounded-full text-[11px] border max-w-[min(100%,280px)]',
        colors.border,
        colors.surface,
        colors.text
      )}
    >
      {icon != null && (
        <span className="inline-flex items-center justify-center w-3.5 h-3.5 shrink-0">{icon}</span>
      )}
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className={cn('p-0.5 rounded shrink-0', colors.interactiveHover)}
        aria-label={`Remove ${label}`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
}

function ActiveFilterChips({
  colors,
  partitionPreset,
  selectedYear,
  dateFilterApplies,
  searchTerm,
  selectedTagId,
  tagName,
  personalFolderName,
  sharedFolderName,
  searchFolderPeriodLabel,
  activeQuickFilters,
  onClearDate,
  onClearSearch,
  onClearTag,
  onClearPersonalFolder,
  onClearSharedFolder,
  onClearQuickFilter,
}: {
  colors: EmailFilterChipTheme;
  partitionPreset: ReceivedDatePreset | null;
  selectedYear: number | null;
  /** When false, date scope is not used for the list (e.g. own personal folder). */
  dateFilterApplies: boolean;
  searchTerm: string;
  selectedTagId: number | undefined;
  tagName: string | undefined;
  personalFolderName: string | undefined;
  sharedFolderName: string | undefined;
  /** Search folder preset period (informational; not removable). */
  searchFolderPeriodLabel?: string | null;
  activeQuickFilters: Set<string>;
  onClearDate: () => void;
  onClearSearch: () => void;
  onClearTag: () => void;
  onClearPersonalFolder: () => void;
  onClearSharedFolder: () => void;
  onClearQuickFilter: (key: string, active: boolean) => void;
}) {
  const dateLabel =
    !dateFilterApplies
      ? null
      : partitionPreset != null
        ? receivedDatePresetLabel(partitionPreset)
        : selectedYear != null
          ? `Year ${selectedYear}`
          : null;

  const searchTrim = searchTerm.trim();
  const quickKeys = Array.from(activeQuickFilters);

  const hasAny =
    dateLabel != null ||
    searchTrim.length > 0 ||
    selectedTagId != null ||
    personalFolderName != null ||
    sharedFolderName != null ||
    (searchFolderPeriodLabel != null && searchFolderPeriodLabel.length > 0) ||
    quickKeys.length > 0;

  if (!hasAny) return null;

  return (
    <div
      className={cn(
        'px-3 py-1.5 flex flex-wrap items-center gap-1.5 border-b text-[11px]',
        colors.border,
        colors.surface
      )}
      role="list"
      aria-label="Active filters"
    >
      <span className={cn('font-medium mr-0.5', colors.textMuted)}>Active:</span>
      {searchFolderPeriodLabel != null && searchFolderPeriodLabel.length > 0 && (
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border',
            colors.border,
            colors.text,
            'bg-indigo-50/80 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800'
          )}
          title="Date filter matches this smart folder period; you can narrow further with the date dropdown"
        >
          Folder period: {searchFolderPeriodLabel}
        </span>
      )}
      {dateLabel != null && (
        <FilterChip
          key="date"
          colors={colors}
          label={dateLabel}
          icon={<CalendarIcon className="w-3.5 h-3.5 text-sky-500" />}
          onRemove={onClearDate}
        />
      )}
      {searchTrim.length > 0 && (
        <FilterChip
          key="search"
          colors={colors}
          label={`Search “${searchTrim}”`}
          icon={<SearchIcon className="w-3.5 h-3.5 text-gray-500" />}
          onRemove={onClearSearch}
        />
      )}
      {selectedTagId != null && (
        <FilterChip
          key="tag"
          colors={colors}
          label={tagName ? `Tag: ${tagName}` : `Tag #${selectedTagId}`}
          onRemove={onClearTag}
        />
      )}
      {personalFolderName != null && (
        <FilterChip key="pf" colors={colors} label={`Folder: ${personalFolderName}`} onRemove={onClearPersonalFolder} />
      )}
      {sharedFolderName != null && (
        <FilterChip
          key="shf"
          colors={colors}
          label={`Shared: ${sharedFolderName}`}
          onRemove={onClearSharedFolder}
        />
      )}
      {quickKeys.map((k) => (
        <FilterChip
          key={`q-${k}`}
          colors={colors}
          label={QUICK_FILTER_CHIP_LABELS[k] ?? k}
          icon={
            k in QUICK_FILTER_CHIP_LABELS ? (
              <QuickFilterIcon filterKey={k as QuickFilterKey} compact />
            ) : undefined
          }
          onRemove={() => onClearQuickFilter(k, false)}
        />
      ))}
    </div>
  );
}

function emailBodyForAi(email: Email | null | undefined): string {
  if (!email) return '';
  const html = email.bodyHtml?.trim();
  if (html) return html;
  const plain = email.bodyText?.trim();
  if (plain) {
    const escaped = plain
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>');
    return `<p>${escaped}</p>`;
  }
  return '';
}

const List: React.FC = () => {
  const { colors } = useTheme();
  const { applicationSettings } = useSettings();
  const enableAiEmails = applicationSettings?.enableAiEmails === true;
  const { prefs: emailUiPrefs, updatePrefs: updateEmailUiPrefs } = useEmailUiPreferences();
  const location = useLocation();
  const navigate = useNavigate();
  const initialListStateRef = useRef(resolveInitialEmailListState(location));
  const initialListState = initialListStateRef.current;
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [pendingCollaborationOpen, setPendingCollaborationOpen] = useState(false);
  
  // View mode: card only (table view removed)
  const viewMode = 'card';

  const [selectedAccount, setSelectedAccount] = useState<number | undefined>(
    initialListState.selectedAccount,
  );
  const [selectedPersonalFolderId, setSelectedPersonalFolderId] = useState<number | undefined>(
    initialListState.selectedPersonalFolderId,
  );
  const [selectedShareId, setSelectedShareId] = useState<number | undefined>(
    initialListState.selectedShareId,
  );
  const [shareFolderOpen, setShareFolderOpen] = useState<{ folderId: number; folderName: string } | null>(null);
  const [shareTagOpen, setShareTagOpen] = useState<{ tagId: number; tagName: string; tagColor?: string | null } | null>(null);
  const [shareTemplateFolderOpen, setShareTemplateFolderOpen] = useState<{ folderId: number; folderName: string } | null>(null);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [createFolderParentId, setCreateFolderParentId] = useState<number | undefined>(undefined);
  const [isRead, setIsRead] = useState<boolean | undefined>();
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(initialListState.selectedEmailId);
  const [selectedPartitionAt, setSelectedPartitionAt] = useState<string | null>(
    initialListState.selectedPartitionAt,
  );
  const [emailListScopeQuery, setEmailListScopeQuery] = useState('');
  const [emailAiDrawerOpen, setEmailAiDrawerOpen] = useState(false);
  const aiReadSelectionRef = useRef('');
  const [splitterPosition, setSplitterPosition] = useState(emailUiPrefs.splitterPosition);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(emailUiPrefs.sidebarWidth);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const handleDisplayPrefsChange = useCallback(
    (next: EmailSidebarDisplayPrefs) => {
      updateEmailUiPrefs({ displayPrefs: next });
    },
    [updateEmailUiPrefs],
  );
  const handleSidebarCollapsedChange = useCallback(
    (collapsed: boolean) => {
      updateEmailUiPrefs({ sidebarCollapsed: collapsed });
    },
    [updateEmailUiPrefs],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [partitionPreset, setPartitionPreset] = useState<ReceivedDatePreset | null>(null);
  /** Bumps on a timer / tab focus so "Recent" presets roll forward without changing every render. */
  const [presetBoundsTick, setPresetBoundsTick] = useState(0);
  /** When true, choosing a tag does not clear the personal folder (both filters apply). */
  /** Stable bounds for SWR key; tick refreshes rolling windows on poll schedule. */
  const presetReceivedQuery = useMemo(() => {
    if (partitionPreset == null) return null;
    const { from, to } = receivedDateRangeForPreset(partitionPreset);
    return { receivedFrom: from.toISOString(), receivedTo: to.toISOString() };
  }, [partitionPreset, presetBoundsTick]);
  const [activeQuickFilters, setActiveQuickFilters] = useState<Set<string>>(new Set());
  const [searchInFields, setSearchInFields] = useState<Set<SearchInField>>(defaultEmailSearchInSet());
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<EmailBulkProgressState | null>(null);
  const bulkActionLoading = bulkProgress != null;
  const [markFolderAsReadOpen, setMarkFolderAsReadOpen] = useState(false);
  const [bulkCopyFolderOpen, setBulkCopyFolderOpen] = useState(false);
  const [bulkTagDropdownOpen, setBulkTagDropdownOpen] = useState(false);
  const bulkTagBtnRef = useRef<HTMLButtonElement>(null);
  const bulkCopyFolderBtnRef = useRef<HTMLButtonElement>(null);
  const [isOnline, setIsOnline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine);
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);
  const { data: accessibleAccounts, isLoading: accountsLoading, mutate: mutateAccounts, updateAccountSidebarColor } = useAccessibleAccounts();
  const { data: sharedAccounts, isLoading: sharedAccountsLoading } = useSharedAccounts();
  /** Mailboxes shared with the current user only (not accounts they own). */
  const accountsSharedWithUser = useMemo(() => {
    const shareByAccountId = new Map(sharedAccounts.map((s) => [s.accountId, s]));
    return accessibleAccounts
      .filter((a) => shareByAccountId.has(a.id))
      .map((a) => {
        const share = shareByAccountId.get(a.id)!;
        return {
          ...a,
          showSentFolder: share.showSentFolder ?? true,
          showMySentFolder: share.showMySentFolder ?? true,
        };
      });
  }, [accessibleAccounts, sharedAccounts]);
  const fullMirrorAccountIds = useMemo(
    () => accountsSharedWithUser.filter((a) => a.imapMirrorMode === 'full_mailbox').map((a) => a.id),
    [accountsSharedWithUser],
  );
  const { data: imapFoldersByAccount } = useImapFoldersForAccounts(fullMirrorAccountIds);
  const { data: personalFolders, createPersonalFolder, updatePersonalFolder, deletePersonalFolder, addEmailToFolder, removeEmailFromFolder, reorderPersonalFolders } = usePersonalFolders();
  const { data: emailTags, createTag: createEmailTag, deleteTag: deleteEmailTag, reorderTags: reorderEmailTags, mutate: mutateEmailTags } = useEmailTags();
  const { data: sharedFolders, shareFolder } = useSharedFolders();
  const { data: sharedSearchFolders, isLoading: sharedSearchFoldersLoading, mutate: mutateSharedSearchFolders } =
    useSharedSearchFolders();
  const { data: unreadCounts, mutate: mutateUnreadCounts } = useUnreadCounts(true);
  const { data: searchFolderUnreadCounts, mutate: mutateSearchFolderUnreadCounts } =
    useSearchFolderUnreadCounts(true);
  const mergedUnreadCounts = useMemo(
    () => ({
      ...unreadCounts,
      bySearchFolder: {
        ...unreadCounts.bySearchFolder,
        ...searchFolderUnreadCounts.bySearchFolder,
      },
      bySearchFolderTotal: {
        ...unreadCounts.bySearchFolderTotal,
        ...searchFolderUnreadCounts.bySearchFolderTotal,
      },
    }),
    [unreadCounts, searchFolderUnreadCounts],
  );
  const revalidateSidebarCounts = () => {
    void mutateUnreadCounts(undefined, { revalidate: true });
    void mutateSearchFolderUnreadCounts(undefined, { revalidate: true });
  };

  const { count: mentionUnreadCount, mutate: mutateMentionUnreadCount } = useMentionUnreadCount();
  const [selectedSharedSearchFolder, setSelectedSharedSearchFolder] = useState<SharedSearchFolderItem | null>(null);
  const [selectedSharedAccount, setSelectedSharedAccount] = useState<SharedAccountItem | null>(null);
  const [selectedSharedAccountFolder, setSelectedSharedAccountFolder] = useState<AccountMailboxFolder>('INBOX');
  /** IMAP mailbox path when viewing an own account (full_mailbox or legacy INBOX) */
  const [selectedImapFolderPath, setSelectedImapFolderPath] = useState<string>(
    initialListState.selectedImapFolderPath,
  );
  const [selectedTagId, setSelectedTagId] = useState<number | undefined>();
  const [selectedSpecialView, setSelectedSpecialView] = useState<EmailSpecialView | null>(
    initialListState.selectedSpecialView,
  );
  const [mentionsViewActive, setMentionsViewActive] = useState(initialListState.mentionsViewActive);
  const activeSearchFolder = useMemo(() => {
    if (selectedSharedSearchFolder) {
      return {
        name: selectedSharedSearchFolder.name,
        periodPreset: selectedSharedSearchFolder.periodPreset,
        periodYear: selectedSharedSearchFolder.periodYear,
      };
    }
    return null;
  }, [selectedSharedSearchFolder]);
  const { showError, showSuccess } = useNotification();
  const {
    company: companyLibrary,
    personal: personalLibrary,
    shared: sharedLibrary,
    isLoading: templateLibraryLoading,
    mutate: mutateTemplateLibrary,
  } = useEmailTemplateLibrary();
  const templateCount =
    companyLibrary.templates.length + personalLibrary.templates.length + sharedLibrary.templates.length;
  const emailsLayoutRef = useRef<HTMLDivElement>(null);
  const sidebarWidthRef = useRef(sidebarWidth);
  sidebarWidthRef.current = sidebarWidth;
  const splitterPositionRef = useRef(splitterPosition);
  splitterPositionRef.current = splitterPosition;

  useEffect(() => {
    setSidebarWidth(emailUiPrefs.sidebarWidth);
    setSplitterPosition(emailUiPrefs.splitterPosition);
  }, [emailUiPrefs.sidebarWidth, emailUiPrefs.splitterPosition]);
  const { mutate: globalMutate } = useSWRConfig();
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const sidebarSelectionReadyRef = useRef(false);
  const mailSelectionBeforeSpecialRef = useRef<EmailSidebarSelectionStored | null>(null);
  const goKeyPendingRef = useRef(false);
  const selectMentionsRef = useRef<(active: boolean) => void>(() => {});
  const composeRestoreAppliedRef = useRef<string | null>(null);
  const listReturnSnapshotRef = useRef({
    search: '',
    selectedEmailId: null as number | null,
    selectedPartitionAt: null as string | null,
    selectedSpecialView: null as EmailSpecialView | null,
    mentionsViewActive: false,
    listPagination: null as EmailListPaginationSnapshot | null,
    captureSelection: (): EmailSidebarSelectionStored | null => null,
  });
  const listPaginationSnapshotRef = useRef<EmailListPaginationSnapshot | null>(
    initialListState.restoreListPagination,
  );

  useEffect(() => {
    if (accountsLoading || sharedAccountsLoading) return;
    const allowed = new Set(accountsSharedWithUser.map((a) => a.id));
    if (selectedAccount != null && !allowed.has(selectedAccount)) {
      setSelectedAccount(undefined);
      setSelectedImapFolderPath('INBOX');
    }
    if (selectedSharedAccount != null && !allowed.has(selectedSharedAccount.accountId)) {
      setSelectedSharedAccount(null);
      setSelectedSharedAccountFolder('INBOX');
    }
  }, [
    accountsLoading,
    sharedAccountsLoading,
    accountsSharedWithUser,
    selectedAccount,
    selectedSharedAccount,
  ]);

  /** When only shared search folders exist (no shared mailboxes), open the first one automatically. */
  useEffect(() => {
    if (sharedAccountsLoading || sharedSearchFoldersLoading || accountsLoading) return;
    if (loadEmailSidebarSelection()) return;
    if (sharedAccounts.length > 0 || accountsSharedWithUser.length > 0) return;
    if (sharedSearchFolders.length === 0) return;
    if (
      selectedSharedSearchFolder != null ||
      selectedAccount != null ||
      selectedPersonalFolderId != null ||
      selectedShareId != null ||
      selectedTagId != null ||
      selectedSpecialView != null
    ) {
      return;
    }
    setSelectedSharedSearchFolder(sharedSearchFolders[0]);
  }, [
    sharedAccountsLoading,
    sharedSearchFoldersLoading,
    accountsLoading,
    sharedAccounts.length,
    accountsSharedWithUser.length,
    sharedSearchFolders,
    selectedSharedSearchFolder,
    selectedAccount,
    selectedPersonalFolderId,
    selectedShareId,
    selectedTagId,
    selectedSpecialView,
  ]);

  const dateScopeMapKey = useMemo(
    () =>
      buildEmailDateScopeMapKey({
        specialView: selectedSpecialView,
        selectedShareId,
        selectedPersonalFolderId,
        selectedSharedSearchFolder,
        selectedAccount,
        selectedSharedAccount,
        selectedSharedAccountFolder,
        selectedTagId,
      }),
    [
      selectedSpecialView,
      selectedShareId,
      selectedPersonalFolderId,
      selectedSharedSearchFolder,
      selectedAccount,
      selectedSharedAccount,
      selectedSharedAccountFolder,
      selectedTagId,
    ],
  );

  useEffect(() => {
    if (partitionPreset == null) return undefined;
    const id = window.setInterval(() => setPresetBoundsTick((t) => t + 1), 60_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') setPresetBoundsTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [partitionPreset]);

  useEffect(() => {
    if (!dateScopeMapKey) return;
    if (selectedPersonalFolderId != null) {
      setPartitionPreset(null);
      setSelectedYear(null);
      return;
    }
    const folderDefault = selectedSharedSearchFolder
      ? searchFolderPeriodToStored(
          selectedSharedSearchFolder.periodPreset,
          selectedSharedSearchFolder.periodYear,
        )
      : null;
    const s = loadEmailDateScopeForKey(dateScopeMapKey) ?? folderDefault;
    if (!s || s.kind === 'any') {
      setPartitionPreset(null);
      setSelectedYear(null);
    } else if (s.kind === 'preset') {
      setPartitionPreset(s.p);
      setSelectedYear(null);
    } else {
      setSelectedYear(s.y);
      setPartitionPreset(null);
    }
  }, [dateScopeMapKey, selectedPersonalFolderId, selectedSharedSearchFolder]);

  const dateScopeMapKeyRef = useRef<string | null>(null);
  dateScopeMapKeyRef.current = dateScopeMapKey;

  const persistEmailDateScopeToStorage = (scope: EmailDateScopeStored) => {
    const key = dateScopeMapKeyRef.current;
    if (!key || (isSpecialEmailView(selectedSpecialView) && selectedSpecialView !== 'mySent' && selectedSpecialView !== 'archived')) return;
    if (selectedPersonalFolderId != null) return;
    saveEmailDateScopeForKey(key, scope);
  };

  // Initialize search term / search-in from URL (used by "Filter messages From/To/By Subject" actions)
  useEffect(() => {
    const s = urlSearchParams.get('search');
    if (s != null) {
      setSearchTerm(s);
    }
    const searchIn = urlSearchParams.get('searchIn');
    if (searchIn) {
      const fields = new Set<SearchInField>(
        searchIn.split(',').map((v) => v.trim()).filter((v): v is SearchInField =>
          ['sender', 'recipients', 'subject', 'body', 'attachments'].includes(v),
        ),
      );
      if (fields.size > 0) {
        setSearchInFields(fields);
      }
    }
    if (urlSearchParams.get('assignedToMe') === 'true') {
      setActiveQuickFilters((prev) => new Set([...Array.from(prev), 'assignedToMe']));
    }
    if (urlSearchParams.get('mentionedMe') === 'true') {
      setMentionsViewActive(true);
    }
    if (urlSearchParams.get('mentionUnreadOnly') === 'true') {
      setActiveQuickFilters((prev) => new Set([...Array.from(prev), 'mentionUnreadOnly']));
    }
    const workflowStatus = urlSearchParams.get('workflowStatus')?.trim().toLowerCase();
    const statusQuickKey = workflowStatus ? WORKFLOW_STATUS_TO_QUICK_FILTER[workflowStatus] : undefined;
    if (statusQuickKey) {
      setActiveQuickFilters((prev) => {
        const next = new Set(prev);
        STATUS_QUICK_FILTER_KEYS.forEach((k) => next.delete(k));
        next.add(statusQuickKey);
        return next;
      });
    }
  }, [urlSearchParams]);

  // Deep link from notifications: /emails?emailId=…&collaboration=1[&partitionAt=…]
  useEffect(() => {
    const emailIdParam = urlSearchParams.get('emailId');
    if (!emailIdParam) return;

    const id = Number.parseInt(emailIdParam, 10);
    if (!Number.isFinite(id) || id <= 0) return;

    setSelectedEmailId(id);
    const partitionAt = urlSearchParams.get('partitionAt');
    if (partitionAt) setSelectedPartitionAt(partitionAt);
    if (urlSearchParams.get('collaboration') === '1') {
      setPendingCollaborationOpen(true);
    }

    const next = new URLSearchParams(urlSearchParams);
    next.delete('emailId');
    next.delete('collaboration');
    if (partitionAt) next.delete('partitionAt');
    setUrlSearchParams(next, { replace: true });
  }, [urlSearchParams, setUrlSearchParams]);

  // When user selects a personal folder, revalidate its list so we always get fresh data from the DB
  // (avoids stale cache after copy/drop from another view)
  useEffect(() => {
    if (selectedPersonalFolderId == null) return;
    globalMutate(
      (key) =>
        typeof key === 'string' &&
        key.startsWith('/emails?') &&
        key.includes(`personalFolderId=${selectedPersonalFolderId}`),
      undefined,
      { revalidate: true }
    );
  }, [selectedPersonalFolderId, globalMutate]);

  // When user switches account or IMAP/shared folder, revalidate list caches (avoids stale data after sync)
  useEffect(() => {
    globalMutate(
      (key) => typeof key === 'string' && key.startsWith('/emails?'),
      undefined,
      { revalidate: true }
    );
  }, [
    selectedAccount,
    selectedSharedAccount,
    selectedSharedAccountFolder,
    selectedImapFolderPath,
    globalMutate,
  ]);

  // Revalidate shared data when Email module mounts (e.g. after sharing from Search Folders page)
  useEffect(() => {
    mutateSharedSearchFolders();
  }, [mutateSharedSearchFolders]);

  // Revalidate lightweight email counters when opening the module (avoid refetching every list cache).
  useEffect(() => {
    globalMutate(
      (key) =>
        typeof key === 'string' &&
        (key.includes('unread-count') ||
          key.includes('mentions/unread-count') ||
          key.startsWith('/emails/accounts/accessible')),
      undefined,
      { revalidate: true },
    );
  }, [globalMutate]);

  // Fetch selected email details
  const { data: selectedEmail, mutate: mutateSelectedEmail } = useEmail(
    selectedEmailId || 0,
    selectedPartitionAt
  );

  const selectedEmailPartitionKey =
    selectedPartitionAt ?? selectedEmail?.partitionAt ?? null;

  const listComposeReturnRef = useRef<ComposeReturnTarget>({ pathname: '/emails' });

  const handleCloseDetail = () => {
    setSelectedEmailId(null);
    setSelectedPartitionAt(null);
  };

  useEffect(() => {
    setEmailAiDrawerOpen(false);
  }, [selectedEmailId]);

  // Global keyboard shortcuts when on emails list/detail (R, F, E, ?)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, select') || target.isContentEditable) return;
      if (!emailsLayoutRef.current?.contains(target)) return;
      const pa = selectedEmailPartitionKey;
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        goKeyPendingRef.current = true;
        window.setTimeout(() => {
          goKeyPendingRef.current = false;
        }, 1200);
        return;
      }
      if (goKeyPendingRef.current && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        goKeyPendingRef.current = false;
        selectMentionsRef.current(true);
        return;
      }
      switch (e.key) {
        case 'r':
        case 'R':
          if (selectedEmailId) {
            e.preventDefault();
            openComposeInNewTab(emailComposeThreadPath(selectedEmailId, 'reply', pa));
          }
          break;
        case 'f':
        case 'F':
          if (selectedEmailId) {
            e.preventDefault();
            openComposeInNewTab(emailComposeThreadPath(selectedEmailId, 'forward', pa));
          }
          break;
        case 'e':
        case 'E':
          if (selectedEmail && selectedEmailId) {
            e.preventDefault();
            navigateToCompose(
              navigate,
              `/emails/compose${composeEditAsNewQuery(selectedEmail.id, selectedEmail.partitionAt)}`,
              { editAsNew: selectedEmail },
              listComposeReturnRef.current,
            );
          }
          break;
        case '?':
          e.preventDefault();
          setShortcutsHelpOpen((open) => !open);
          break;
        case 'Escape':
          if (shortcutsHelpOpen) {
            e.preventDefault();
            setShortcutsHelpOpen(false);
            break;
          }
          if (emailAiDrawerOpen) break;
          if (selectedEmailId != null) {
            e.preventDefault();
            handleCloseDetail();
          }
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, selectedEmailId, selectedEmail, selectedEmailPartitionKey, shortcutsHelpOpen, emailAiDrawerOpen, handleCloseDetail]);

  const handleToggleRead = async (email: Email) => {
    try {
      const pQ = emailPartitionAtSearch(email.partitionAt);
      if (email.isRead) {
        await api.post(`/emails/${email.id}/mark-unread${pQ}`);
      } else {
        await api.post(`/emails/${email.id}/mark-read${pQ}`);
      }
      const updated = { ...email, isRead: !email.isRead };
      if (selectedEmailId === email.id) {
        mutateSelectedEmail();
      }
      if (listMutateRef.current) {
        listMutateRef.current(
          (prev: EmailsResponse | undefined): EmailsResponse | undefined => {
            if (!prev?.data) return prev;
            const rows = prev.data as Array<Email | EmailThreadGroup>;
            return {
              ...prev,
              data: rows.map((item) => {
                if ('id' in item && item.id === email.id) {
                  return { ...item, isRead: updated.isRead };
                }
                if ('latestEmail' in item && item.latestEmail.id === email.id) {
                  return {
                    ...item,
                    latestEmail: { ...item.latestEmail, isRead: updated.isRead },
                  };
                }
                return item;
              }) as EmailsResponse['data'],
            };
          },
          false
        );
        listMutateRef.current();
      }
      revalidateSidebarCounts();
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to update read status');
    }
  };

  const dateScopeAppliesToEmailList =
    (!isSpecialEmailView(selectedSpecialView) ||
      selectedSpecialView === 'mySent' ||
      selectedSpecialView === 'archived') &&
    !mentionsViewActive &&
    selectedPersonalFolderId == null;

  const isSpecialListView = isCustomPanelSpecialView(selectedSpecialView);

  const isDesktopEmailLayout = useMediaQuery(MEDIA_QUERIES.desktopAndUp);
  const isNarrowEmailLayout = !isDesktopEmailLayout;
  const showEmailListPane = isSpecialListView || !isNarrowEmailLayout || selectedEmailId == null;
  const showEmailDetailPane = !isSpecialListView && (!isNarrowEmailLayout || selectedEmailId != null);

  const showMailboxLocation =
    selectedSpecialView === 'archived' ||
    (!isSpecialListView &&
      !(
        selectedAccount != null &&
        selectedSharedSearchFolder == null &&
        selectedTagId == null &&
        selectedPersonalFolderId == null &&
        selectedShareId == null &&
        selectedSharedSearchFolder == null &&
        selectedSharedAccount == null
      ));

  const useEmailsForList = (searchParams?: string, options?: EmailsListOptions) => {
    const queryParams = new URLSearchParams();
    const isMySentSmartFolder =
      selectedSharedSearchFolder != null &&
      searchFolderSentByMe(selectedSharedSearchFolder.folderKind);
    if (selectedSpecialView !== 'mySent' && !isMySentSmartFolder) {
      // Sidebar mailboxes are shared-with-me; My sent uses all accessible accounts for the current user.
      queryParams.set('sharedOnly', 'true');
    }
    if (mentionsViewActive) {
      queryParams.set('mentionedMe', 'true');
      if (activeQuickFilters.has('mentionUnreadOnly')) {
        queryParams.set('mentionUnreadOnly', 'true');
      }
    } else if (selectedSpecialView === 'drafts') {
      queryParams.set('folder', 'DRAFT');
    } else if (selectedSpecialView === 'templates') {
      queryParams.set('folder', '__NONE__');
    } else if (selectedSpecialView === 'mySent') {
      queryParams.set('folder', 'SENT');
      queryParams.set('sentByMe', 'true');
    }

    if (
      !mentionsViewActive &&
      selectedSpecialView !== 'drafts' &&
      selectedSpecialView !== 'templates' &&
      selectedSpecialView !== 'outbox' &&
      selectedSpecialView !== 'mySent' &&
      selectedSpecialView !== 'archived'
    ) {
      const hasMailListScope =
        selectedShareId != null ||
        selectedPersonalFolderId != null ||
        selectedSharedSearchFolder != null ||
        selectedAccount != null ||
        selectedSharedAccount != null;
      if (!hasMailListScope) {
        queryParams.set('folder', '__NONE__');
      } else if (selectedShareId) {
        queryParams.append('shareId', selectedShareId.toString());
      } else if (selectedPersonalFolderId) {
        queryParams.append('personalFolderId', selectedPersonalFolderId.toString());
      } else {
        if (selectedSharedSearchFolder) {
          queryParams.append('sharedSearchFolderShareId', selectedSharedSearchFolder.shareId.toString());
          const search = typeof selectedSharedSearchFolder.search === 'string'
            ? selectedSharedSearchFolder.search.trim()
            : '';
          if (search) {
            queryParams.append('baseSearch', search);
          }
          const searchInRaw = selectedSharedSearchFolder.searchIn;
          const searchInArr = Array.isArray(searchInRaw)
            ? searchInRaw
            : typeof searchInRaw === 'string'
              ? (() => {
                  try {
                    const parsed = JSON.parse(searchInRaw);
                    return Array.isArray(parsed) ? parsed : [];
                  } catch {
                    return (searchInRaw as string).split(',').map((s) => s.trim()).filter(Boolean);
                  }
                })()
              : [];
          if (searchInArr.length > 0) {
            queryParams.append('baseSearchIn', searchInArr.join(','));
          }
          if (selectedSharedSearchFolder.accountIds != null && selectedSharedSearchFolder.accountIds.length > 1) {
            queryParams.append('baseAccountIds', selectedSharedSearchFolder.accountIds.join(','));
          } else if (selectedSharedSearchFolder.accountId != null) {
            queryParams.append('baseAccountId', selectedSharedSearchFolder.accountId.toString());
          }
          // Only send folder when the search folder is scoped to a specific folder (not "All folders")
          if (selectedSharedSearchFolder.folder) {
            queryParams.append('baseFolder', selectedSharedSearchFolder.folder);
            queryParams.append('folder', selectedSharedSearchFolder.folder);
          }
          if (isMySentSmartFolder) {
            queryParams.append('sentByMe', 'true');
          }
        }
        if (!selectedSharedSearchFolder) {
          const mailAccountId = selectedAccount ?? selectedSharedAccount?.accountId;
          if (mailAccountId) {
            queryParams.append('accountId', mailAccountId.toString());
            const mailboxFolder: AccountMailboxFolder | string = selectedSharedAccount
              ? selectedSharedAccountFolder
              : selectedImapFolderPath || 'INBOX';
            if (mailboxFolder === 'MY_SENT') {
              queryParams.append('folder', 'SENT');
              queryParams.append('sentByMe', 'true');
            } else {
              queryParams.append('folder', mailboxFolder);
            }
          }
        }
      }
    }

    // Search folder = base filter; UI filters (quick filters, search bar) apply as additional filter on top
    const hasSearchFolder = selectedSharedSearchFolder != null;
    if (dateScopeAppliesToEmailList && presetReceivedQuery != null) {
      queryParams.set('receivedFrom', presetReceivedQuery.receivedFrom);
      queryParams.set('receivedTo', presetReceivedQuery.receivedTo);
    } else if (dateScopeAppliesToEmailList && selectedYear != null) {
      const from = new Date(Date.UTC(selectedYear, 0, 1, 0, 0, 0, 0)).toISOString();
      const to = new Date(Date.UTC(selectedYear + 1, 0, 1, 0, 0, 0, 0)).toISOString();
      queryParams.set('from', from);
      queryParams.set('to', to);
    }
    if (activeQuickFilters.has('unread')) {
      queryParams.set('isRead', 'false');
    } else if (isRead !== undefined) {
      queryParams.set('isRead', isRead.toString());
    }
    // Archived folder view shows archived only; all other lists exclude archived by default.
    if (selectedSpecialView === 'archived') {
      queryParams.set('isArchived', 'true');
    } else {
      queryParams.set('isArchived', activeQuickFilters.has('archived') ? 'true' : 'false');
    }
    if (activeQuickFilters.has('starred')) {
      queryParams.set('isStarred', 'true');
    }
    if (activeQuickFilters.has('assignedToMe')) {
      queryParams.set('assignedToMe', 'true');
    }
    for (const statusKey of STATUS_QUICK_FILTER_KEYS) {
      if (activeQuickFilters.has(statusKey)) {
        queryParams.set('workflowStatus', STATUS_QUICK_FILTER_TO_VALUE[statusKey]);
        break;
      }
    }
    if (activeQuickFilters.has('attachments')) {
      queryParams.set('hasAttachments', 'true');
    }
    if (activeQuickFilters.has('linked')) {
      queryParams.set('hasLinks', 'true');
    }
    if (selectedTagId != null) {
      queryParams.set('tagId', selectedTagId.toString());
    }
    if (searchTerm) {
      if (hasSearchFolder) {
        queryParams.append('extraSearch', searchTerm);
        const searchInArray = Array.from(searchInFields);
        if (searchInArray.length > 0) {
          queryParams.append('extraSearchIn', searchInArray.join(','));
        }
      } else {
        queryParams.append('search', searchTerm);
        const searchInArray = Array.from(searchInFields);
        if (searchInArray.length > 0) {
          queryParams.append('searchIn', searchInArray.join(','));
        }
      }
    } else if (searchParams && !hasSearchFolder) {
      queryParams.append('search', searchParams);
    }
    if (emailUiPrefs.threadedView && !mentionsViewActive) {
      queryParams.set('groupBy', 'thread');
    }
    if (!mentionsViewActive) {
      queryParams.set('sortBy', emailUiPrefs.listSortBy);
    }

    const fullParams = queryParams.toString();
    return useEmails(fullParams ? fullParams : undefined, options);
  };

  // Ref to the list's mutate (table/card use useEmailsForList with skip/take = different SWR key than parent)
  const listMutateRef = useRef<EmailsListMutate | null>(null);

  // Get mutate function from emails list hook (for result count etc.)
  const emailsListResult = useEmailsForList(searchTerm);
  const emailsData = emailsListResult.data || [];

  const imapPathToMailboxFolder = (path: string): AccountMailboxFolder => {
    if (path === 'MY_SENT') return 'MY_SENT';
    if (path === 'SENT') return 'SENT';
    if (path === 'DRAFT') return 'DRAFT';
    if (path === 'JUNK') return 'JUNK';
    return 'INBOX';
  };

  const mailboxFolderDisplayName = (folder: AccountMailboxFolder | string): string => {
    switch (folder) {
      case 'INBOX':
        return 'Inbox';
      case 'SENT':
        return 'Sent';
      case 'MY_SENT':
        return 'My sent';
      case 'JUNK':
        return 'Junk';
      case 'DRAFT':
        return 'Draft';
      default:
        return String(folder);
    }
  };

  const markFolderAsReadTarget = useMemo((): MarkFolderAsReadTarget | null => {
    const blockedSpecial =
      selectedSpecialView === 'drafts' ||
      selectedSpecialView === 'templates' ||
      selectedSpecialView === 'outbox';
    if (blockedSpecial) return null;

    const counts = mergedUnreadCounts;

    if (mentionsViewActive) {
      return {
        folderName: 'Mentions',
        unreadEstimate: mentionUnreadCount,
        filters: { mentionedMe: true, sharedOnly: true },
      };
    }

    if (selectedSpecialView === 'archived') {
      return {
        folderName: 'Archived',
        unreadEstimate: 0,
        filters: { isArchived: true, sharedOnly: true },
      };
    }

    if (selectedSpecialView === 'mySent') {
      return {
        folderName: 'My sent',
        unreadEstimate: 0,
        filters: { folder: 'SENT', sentByMe: true, isArchived: false },
      };
    }

    if (selectedShareId != null) {
      const shared = flattenSharedFolderNodes(sharedFolders ?? []).find(
        (f) => f.shareId === selectedShareId,
      );
      return {
        folderName: shared?.name ?? 'Shared folder',
        unreadEstimate: 0,
        filters: { shareId: selectedShareId, isArchived: false, sharedOnly: true },
      };
    }

    if (selectedPersonalFolderId != null) {
      const folder = flattenPersonalFolders(personalFolders ?? []).find(
        (f) => f.id === selectedPersonalFolderId,
      );
      const unread = counts.byPersonalFolder[String(selectedPersonalFolderId)] ?? 0;
      return {
        folderName: folder?.name ?? 'Personal folder',
        unreadEstimate: unread,
        filters: {
          personalFolderId: selectedPersonalFolderId,
          isArchived: false,
          sharedOnly: true,
        },
      };
    }

    if (selectedSharedSearchFolder != null) {
      const sf = selectedSharedSearchFolder;
      const unread = counts.bySearchFolder[String(sf.folderId)] ?? 0;
      const searchInArr = Array.isArray(sf.searchIn)
        ? sf.searchIn
        : typeof sf.searchIn === 'string'
          ? (() => {
              try {
                const parsed = JSON.parse(sf.searchIn as string);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return String(sf.searchIn)
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean);
              }
            })()
          : [];
      const filters: MarkFolderAsReadFilters = {
        sharedSearchFolderShareId: sf.shareId,
        isArchived: false,
        sharedOnly: !searchFolderSentByMe(sf.folderKind),
      };
      const search = typeof sf.search === 'string' ? sf.search.trim() : '';
      if (search) filters.baseSearch = search;
      if (searchInArr.length > 0) filters.baseSearchIn = searchInArr.join(',');
      if (sf.accountIds != null && sf.accountIds.length > 1) {
        filters.baseAccountIds = sf.accountIds.join(',');
      } else if (sf.accountId != null) {
        filters.baseAccountId = sf.accountId;
      }
      if (sf.folder) filters.baseFolder = sf.folder;
      if (searchFolderSentByMe(sf.folderKind)) filters.sentByMe = true;
      return {
        folderName: sf.name,
        unreadEstimate: unread,
        filters,
      };
    }

    if (selectedSharedAccount != null) {
      const folder = selectedSharedAccountFolder;
      const folderKey = `${selectedSharedAccount.accountId}:${folder === 'MY_SENT' ? 'MY_SENT' : folder}`;
      const unread = counts.byAccountFolder[folderKey] ?? 0;
      const filters: MarkFolderAsReadFilters = {
        accountId: selectedSharedAccount.accountId,
        isArchived: false,
        sharedOnly: true,
      };
      if (folder === 'MY_SENT') {
        filters.folder = 'SENT';
        filters.sentByMe = true;
      } else {
        filters.folder = folder;
      }
      return {
        folderName: mailboxFolderDisplayName(folder),
        unreadEstimate: unread,
        filters,
      };
    }

    if (selectedAccount != null) {
      const path = selectedImapFolderPath || 'INBOX';
      const mailbox = imapPathToMailboxFolder(path);
      const imapUnread = counts.byAccountImapFolder[String(selectedAccount)]?.[path];
      const folderKey = `${selectedAccount}:${mailbox === 'MY_SENT' ? 'MY_SENT' : mailbox}`;
      const unread = imapUnread ?? counts.byAccountFolder[folderKey] ?? 0;
      const filters: MarkFolderAsReadFilters = {
        accountId: selectedAccount,
        isArchived: false,
        sharedOnly: true,
      };
      if (mailbox === 'MY_SENT') {
        filters.folder = 'SENT';
        filters.sentByMe = true;
      } else {
        filters.folder = path;
      }
      return {
        folderName: mailboxFolderDisplayName(mailbox === 'INBOX' && path !== 'INBOX' ? path : mailbox),
        unreadEstimate: unread,
        filters,
      };
    }

    return null;
  }, [
    selectedSpecialView,
    mentionsViewActive,
    mentionUnreadCount,
    selectedShareId,
    sharedFolders,
    selectedPersonalFolderId,
    personalFolders,
    selectedSharedSearchFolder,
    selectedSharedAccount,
    selectedSharedAccountFolder,
    selectedAccount,
    selectedImapFolderPath,
    mergedUnreadCounts,
  ]);

  const handleSelectInbox = (accountId: number, folder: AccountMailboxFolder = 'INBOX') => {
    setMentionsViewActive(false);
    setSelectedSpecialView(null);
    setSelectedAccount(accountId);
    setSelectedImapFolderPath(folder);
    setSelectedPersonalFolderId(undefined);
    setSelectedShareId(undefined);
    setSelectedTagId(undefined);
    setSelectedSharedSearchFolder(null);
    setSelectedSharedAccount(null);
    setSearchTerm('');
    setSearchInFields(defaultEmailSearchInSet());
    const share = sharedAccounts.find((s) => s.accountId === accountId);
    if (share) {
      persistSidebarSelection({
        kind: 'sharedAccount',
        shareId: share.shareId,
        accountId,
        folder,
        imapPath: folder,
      });
    }
  };

  const handleSelectImapFolder = (accountId: number, imapPath: string) => {
    setMentionsViewActive(false);
    setSelectedSpecialView(null);
    setSelectedAccount(accountId);
    setSelectedImapFolderPath(imapPath);
    setSelectedPersonalFolderId(undefined);
    setSelectedShareId(undefined);
    setSelectedTagId(undefined);
    setSelectedSharedSearchFolder(null);
    setSelectedSharedAccount(null);
    setSearchTerm('');
    setSearchInFields(defaultEmailSearchInSet());
    const share = sharedAccounts.find((s) => s.accountId === accountId);
    if (share) {
      persistSidebarSelection({
        kind: 'sharedAccount',
        shareId: share.shareId,
        accountId,
        folder: imapPathToMailboxFolder(imapPath),
        imapPath,
      });
    }
  };

  const handleSelectPersonalFolder = (folder: EmailPersonalFolderTreeNode | null) => {
    setMentionsViewActive(false);
    setSelectedSpecialView(null);
    setSelectedPersonalFolderId(folder?.id);
    setSelectedShareId(undefined);
    setSelectedTagId(undefined);
    setSelectedSharedSearchFolder(null);
    setSelectedSharedAccount(null);
    setSelectedAccount(undefined);
    setSelectedImapFolderPath('INBOX');
    setSearchTerm('');
    setSearchInFields(defaultEmailSearchInSet());
    if (folder?.id != null) {
      persistSidebarSelection({ kind: 'personalFolder', id: folder.id });
    }
  };

  const handleSelectSharedFolder = (share: { shareId: number } | null) => {
    setMentionsViewActive(false);
    setSelectedSpecialView(null);
    setSelectedShareId(share?.shareId);
    setSelectedPersonalFolderId(undefined);
    setSelectedTagId(undefined);
    setSelectedAccount(undefined);
    setSelectedImapFolderPath('INBOX');
    setSelectedSharedSearchFolder(null);
    setSelectedSharedAccount(null);
    setSearchTerm('');
    setSearchInFields(defaultEmailSearchInSet());
    if (share?.shareId != null) {
      persistSidebarSelection({ kind: 'sharedFolder', shareId: share.shareId });
    }
  };

  const handleSelectSharedSearchFolder = (share: SharedSearchFolderItem | null) => {
    setMentionsViewActive(false);
    setSelectedSpecialView(null);
    setSelectedSharedSearchFolder(share);
    setSelectedAccount(undefined);
    setSelectedSharedAccount(null);
    setSelectedShareId(undefined);
    setSelectedPersonalFolderId(undefined);
    setSelectedTagId(undefined);
    setSelectedImapFolderPath('INBOX');
    if (share?.shareId != null) {
      persistSidebarSelection({ kind: 'sharedSearchFolder', shareId: share.shareId });
    }
  };

  const handleSelectSharedAccount = (share: SharedAccountItem | null, folder?: AccountMailboxFolder) => {
    setMentionsViewActive(false);
    setSelectedSpecialView(null);
    setSelectedSharedAccount(share);
    setSelectedSharedAccountFolder(folder ?? 'INBOX');
    setSelectedSharedSearchFolder(null);
    setSelectedShareId(undefined);
    setSelectedPersonalFolderId(undefined);
    setSelectedTagId(undefined);
    setSelectedAccount(share?.accountId);
    setSelectedImapFolderPath('INBOX');
    setSearchTerm('');
    setSearchInFields(defaultEmailSearchInSet());
    if (share) {
      persistSidebarSelection({
        kind: 'sharedAccount',
        shareId: share.shareId,
        accountId: share.accountId,
        folder: folder ?? 'INBOX',
        imapPath: folder ?? 'INBOX',
      });
    }
  };

  const captureCurrentMailSelection = (): EmailSidebarSelectionStored | null => {
    if (mentionsViewActive) return { kind: 'mentions' };
    if (selectedShareId != null) return { kind: 'sharedFolder', shareId: selectedShareId };
    if (selectedPersonalFolderId != null) return { kind: 'personalFolder', id: selectedPersonalFolderId };
    if (selectedSharedSearchFolder != null) {
      return { kind: 'sharedSearchFolder', shareId: selectedSharedSearchFolder.shareId };
    }
    if (selectedSharedAccount != null) {
      return {
        kind: 'sharedAccount',
        shareId: selectedSharedAccount.shareId,
        accountId: selectedSharedAccount.accountId,
        folder: selectedSharedAccountFolder,
        imapPath: selectedImapFolderPath,
      };
    }
    if (selectedAccount != null) {
      const share = sharedAccounts.find((s) => s.accountId === selectedAccount);
      if (share) {
        return {
          kind: 'sharedAccount',
          shareId: share.shareId,
          accountId: selectedAccount,
          folder: imapPathToMailboxFolder(selectedImapFolderPath),
          imapPath: selectedImapFolderPath,
        };
      }
    }
    const stored = loadEmailSidebarSelection();
    if (stored && stored.kind !== 'special' && stored.kind !== 'mentions' && stored.kind !== 'all') return stored;
    return null;
  };

  const restoreDefaultMailSelection = () => {
    const firstAccount = accountsSharedWithUser[0];
    if (firstAccount) {
      handleSelectInbox(firstAccount.id, 'INBOX');
    } else if (sharedSearchFolders.length > 0) {
      handleSelectSharedSearchFolder(sharedSearchFolders[0]);
    }
  };

  const applySidebarSelection = (stored: EmailSidebarSelectionStored) => {
    switch (stored.kind) {
      case 'sharedAccount': {
        const share = sharedAccounts.find((s) => s.shareId === stored.shareId);
        if (!share) {
          restoreDefaultMailSelection();
          break;
        }
        const standardImapPaths = new Set(['INBOX', 'DRAFT', 'SENT', 'MY_SENT', 'JUNK']);
        if (stored.imapPath && !standardImapPaths.has(stored.imapPath)) {
          handleSelectImapFolder(share.accountId, stored.imapPath);
        } else {
          handleSelectSharedAccount(share, stored.folder);
        }
        break;
      }
      case 'searchFolder': {
        const ssf = sharedSearchFolders.find((s) => s.folderId === stored.id);
        if (ssf) handleSelectSharedSearchFolder(ssf);
        else restoreDefaultMailSelection();
        break;
      }
      case 'sharedSearchFolder': {
        const ssf = sharedSearchFolders.find((s) => s.shareId === stored.shareId);
        if (ssf) handleSelectSharedSearchFolder(ssf);
        else restoreDefaultMailSelection();
        break;
      }
      case 'personalFolder': {
        const node = personalFolders?.find((n) => n.id === stored.id);
        if (node) handleSelectPersonalFolder(node);
        else restoreDefaultMailSelection();
        break;
      }
      case 'sharedFolder':
        handleSelectSharedFolder({ shareId: stored.shareId });
        break;
      case 'mentions':
        handleSelectMentionsView(true);
        break;
      default:
        restoreDefaultMailSelection();
        break;
    }
  };

  const handleSelectMentionsView = (active: boolean) => {
    if (active) {
      if (!mentionsViewActive && selectedSpecialView == null) {
        mailSelectionBeforeSpecialRef.current = captureCurrentMailSelection();
      }
      setMentionsViewActive(true);
      setSelectedSpecialView(null);
      setSelectedAccount(undefined);
      setSelectedPersonalFolderId(undefined);
      setSelectedShareId(undefined);
      setSelectedSharedSearchFolder(null);
      setSelectedSharedAccount(null);
      setSelectedSharedAccountFolder('INBOX');
      setSelectedImapFolderPath('INBOX');
      setSelectedTagId(undefined);
      setSearchTerm('');
      setSearchInFields(defaultEmailSearchInSet());
      setSelectedEmailId(null);
      setSelectedPartitionAt(null);
      persistSidebarSelection({ kind: 'mentions' });
      return;
    }

    setMentionsViewActive(false);
    setSelectedEmailId(null);
    setSelectedPartitionAt(null);
    const prior = mailSelectionBeforeSpecialRef.current;
    mailSelectionBeforeSpecialRef.current = null;
    if (prior && prior.kind !== 'mentions' && prior.kind !== 'special') {
      applySidebarSelection(prior);
      return;
    }
    restoreDefaultMailSelection();
  };

  selectMentionsRef.current = handleSelectMentionsView;

  const handleSelectSpecialView = (view: EmailSpecialView | null) => {
    if (view != null) {
      setMentionsViewActive(false);
      if (selectedSpecialView == null && !mentionsViewActive) {
        mailSelectionBeforeSpecialRef.current = captureCurrentMailSelection();
      }
      setSelectedSpecialView(view);
      setSelectedAccount(undefined);
      setSelectedPersonalFolderId(undefined);
      setSelectedShareId(undefined);
      setSelectedSharedSearchFolder(null);
      setSelectedSharedAccount(null);
      setSelectedSharedAccountFolder('INBOX');
      setSelectedImapFolderPath('INBOX');
      setSelectedTagId(undefined);
      setSearchTerm('');
      setSearchInFields(defaultEmailSearchInSet());
      if (view === 'archived') {
        setActiveQuickFilters((prev) => {
          if (!prev.has('archived')) return prev;
          const next = new Set(prev);
          next.delete('archived');
          return next;
        });
      }
      setSelectedEmailId(null);
      setSelectedPartitionAt(null);
      persistSidebarSelection({ kind: 'special', view });
      return;
    }

    setSelectedSpecialView(null);
    setSelectedEmailId(null);
    setSelectedPartitionAt(null);

    const prior = mailSelectionBeforeSpecialRef.current;
    mailSelectionBeforeSpecialRef.current = null;
    if (prior) {
      applySidebarSelection(prior);
      return;
    }
    restoreDefaultMailSelection();
  };

  listReturnSnapshotRef.current = {
    search: location.search,
    selectedEmailId,
    selectedPartitionAt,
    selectedSpecialView,
    mentionsViewActive,
    listPagination: listPaginationSnapshotRef.current,
    captureSelection: captureCurrentMailSelection,
  };

  listComposeReturnRef.current = composeReturnFromEmailsList(
    location,
    selectedEmailId,
    selectedPartitionAt,
    selectedSpecialView,
    captureCurrentMailSelection(),
    listPaginationSnapshotRef.current,
  );
  const listComposeReturn = listComposeReturnRef.current;

  useEffect(() => {
    return () => {
      const snap = listReturnSnapshotRef.current;
      const sidebarSelection = snap.captureSelection();
      if (sidebarSelection) {
        saveEmailSidebarSelection(sidebarSelection);
      }
      saveEmailListReturnContext({
        search: snap.search,
        sidebarSelection,
        restoreSelectedEmailId: snap.selectedEmailId,
        restorePartitionAt: snap.selectedPartitionAt,
        restoreSpecialView: snap.selectedSpecialView,
        mentionsViewActive: snap.mentionsViewActive,
        restoreListPagination: snap.listPagination,
      });
    };
  }, []);

  /** Restore folder + list context after returning from compose, detail, or browser back. */
  useEffect(() => {
    if (sharedAccountsLoading || sharedSearchFoldersLoading || accountsLoading) return;
    if (sidebarSelectionReadyRef.current) return;

    const state = location.state as ComposeListRestoreState | null;
    const sessionCtx = loadEmailListReturnContext();

    const resolveSidebarSelection = (): EmailSidebarSelectionStored | null => {
      if (state?.restoreSidebarSelection) return state.restoreSidebarSelection;
      if (state?.restoreSpecialView) return { kind: 'special', view: state.restoreSpecialView };
      if (state?.restoreMentionsView) return { kind: 'mentions' };
      if (sessionCtx?.sidebarSelection) return sessionCtx.sidebarSelection;
      const stored = loadEmailSidebarSelection();
      if (stored && stored.kind !== 'all') return stored;
      return null;
    };

    const sidebar = resolveSidebarSelection();

    const sidebarNeedsSharedAccounts =
      sidebar == null ||
      sidebar.kind === 'sharedAccount' ||
      sidebar.kind === 'sharedSearchFolder' ||
      sidebar.kind === 'searchFolder';
    const sharedAccountsReady =
      accountsSharedWithUser.length > 0 || sharedSearchFolders.length > 0;
    const bootstrappedMailScope =
      initialListState.selectedAccount != null ||
      initialListState.selectedPersonalFolderId != null ||
      initialListState.selectedShareId != null ||
      initialListState.mentionsViewActive ||
      initialListState.selectedSpecialView != null;

    if (sidebarNeedsSharedAccounts && !sharedAccountsReady && !bootstrappedMailScope) {
      return;
    }

    const explicitSidebar =
      state?.restoreSidebarSelection != null || sessionCtx?.sidebarSelection != null;

    if (sidebar?.kind === 'special') {
      handleSelectSpecialView(sidebar.view);
    } else if (sidebar?.kind === 'mentions') {
      handleSelectMentionsView(true);
    } else if (sidebar && (!bootstrappedMailScope || explicitSidebar)) {
      applySidebarSelection(sidebar);
    } else if (!sidebar && !bootstrappedMailScope) {
      restoreDefaultMailSelection();
      if (!sharedAccountsReady) return;
    }

    sidebarSelectionReadyRef.current = true;

    const restoreFingerprint = JSON.stringify({
      id: state?.restoreSelectedEmailId,
      pa: state?.restorePartitionAt,
      sid: state?.restoreSidebarSelection,
    });
    const hasLocationRestore =
      state?.restoreSelectedEmailId != null ||
      state?.restoreSpecialView != null ||
      state?.restoreMentionsView != null ||
      state?.restoreSidebarSelection != null ||
      state?.restoreListPagination != null;

    if (hasLocationRestore && composeRestoreAppliedRef.current !== restoreFingerprint) {
      composeRestoreAppliedRef.current = restoreFingerprint;
      if (state?.restoreSelectedEmailId != null) {
        setSelectedEmailId(state.restoreSelectedEmailId);
        setSelectedPartitionAt(state.restorePartitionAt ?? null);
      }
      const {
        restoreSelectedEmailId: _id,
        restorePartitionAt: _pa,
        restoreSpecialView: _sv,
        restoreMentionsView: _mv,
        restoreSidebarSelection: _sb,
        restoreListPagination: _lp,
        fromComposeSuccess: _ok,
        ...rest
      } = state ?? {};
      const nextState = Object.keys(rest).length > 0 ? rest : null;
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: nextState },
      );
    } else if (sessionCtx && !hasLocationRestore) {
      const needsSearch = (sessionCtx.search || '') !== (location.search || '');
      const needsEmailRestore =
        sessionCtx.restoreSelectedEmailId != null ||
        sessionCtx.restoreSpecialView != null ||
        sessionCtx.mentionsViewActive ||
        sessionCtx.restoreListPagination != null;
      clearEmailListReturnContext();
      if (needsSearch || needsEmailRestore) {
        sidebarSelectionReadyRef.current = false;
        composeRestoreAppliedRef.current = null;
        navigate(
          { pathname: '/emails', search: sessionCtx.search || '' },
          {
            replace: true,
            state: {
              restoreSelectedEmailId: sessionCtx.restoreSelectedEmailId ?? undefined,
              restorePartitionAt: sessionCtx.restorePartitionAt ?? undefined,
              restoreSpecialView: sessionCtx.restoreSpecialView ?? undefined,
              restoreMentionsView: sessionCtx.mentionsViewActive ? true : undefined,
              restoreSidebarSelection: sessionCtx.sidebarSelection ?? undefined,
              restoreListPagination: sessionCtx.restoreListPagination ?? undefined,
            },
          },
        );
        return;
      }
      if (sessionCtx.restoreSelectedEmailId != null) {
        setSelectedEmailId(sessionCtx.restoreSelectedEmailId);
        setSelectedPartitionAt(sessionCtx.restorePartitionAt ?? null);
      }
    } else if (sessionCtx) {
      clearEmailListReturnContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sharedAccountsLoading,
    sharedSearchFoldersLoading,
    accountsLoading,
    location.pathname,
    location.search,
    location.hash,
    location.state,
    navigate,
    sharedAccounts,
    sharedSearchFolders,
    personalFolders,
    sharedFolders,
  ]);

  const listEmptyHint = useMemo(() => {
    if (selectedSpecialView === 'mySent') {
      return 'Send from this app (pick a send-as identity in Compose) to appear here. If you send from another mailbox, link it under Email accounts → Also show Sent from accounts.';
    }
    if (selectedSpecialView === 'archived') {
      return 'No archived messages. Archive a message from its detail view or the list actions menu.';
    }
    if (mentionsViewActive) {
      return activeQuickFilters.has('mentionUnreadOnly')
        ? 'No unread mentions. You are all caught up.'
        : 'No @mentions yet. When someone mentions you in collaboration notes, they appear here.';
    }
    const folder =
      selectedSharedAccount != null
        ? selectedSharedAccountFolder
        : selectedAccount != null &&
            (selectedImapFolderPath === 'SENT' || selectedImapFolderPath === 'MY_SENT')
          ? (selectedImapFolderPath as AccountMailboxFolder)
          : null;
    if (folder === 'MY_SENT') {
      return 'Send from this app (pick a send-as identity in Compose) to appear here. If you send from another mailbox, link it under Email accounts → Also show Sent from accounts.';
    }
    if (folder === 'SENT') {
      return 'No sent mail yet. Sync the account if messages should appear from your mail server.';
    }
    return undefined;
  }, [mentionsViewActive, activeQuickFilters, selectedSharedAccount, selectedSharedAccountFolder, selectedAccount, selectedImapFolderPath, selectedSpecialView]);

  const activeSearchFolderPeriodChip =
    activeSearchFolder != null
      ? searchFolderPeriodShortLabel(activeSearchFolder.periodPreset, activeSearchFolder.periodYear)
      : null;

  const handleShareFolder = (folder: EmailPersonalFolderTreeNode) => {
    setShareFolderOpen({ folderId: folder.id, folderName: folder.name });
  };

  const handleShareTag = (tag: { id: number; name: string; color?: string | null }) => {
    setShareTagOpen({ tagId: tag.id, tagName: tag.name, tagColor: tag.color });
  };

  const handleOpenCreateFolderModal = (parentId?: number) => {
    setCreateFolderParentId(parentId);
    setCreateFolderModalOpen(true);
  };

  const handleCreatePersonalFolder = async (body: { name: string; parentId?: number }) => {
    try {
      await createPersonalFolder(body);
      showSuccess('Folder created');
    } catch (err: any) {
      throw err; // let modal show error
    }
  };

  // Handle quick filter toggle
  const handleMarkAllMentionsRead = async () => {
    try {
      await markAllMentionsRead();
      await revalidateMentionCaches(globalMutate);
      mutateMentionUnreadCount();
      listMutateRef.current?.();
      showSuccess('All mentions marked as read');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to mark mentions as read');
    }
  };

  const handleQuickFilter = (filterKey: string, active: boolean) => {
    setActiveQuickFilters((prev) => {
      const newSet = new Set(prev);
      if (active) {
        if (STATUS_QUICK_FILTER_KEYS.includes(filterKey as (typeof STATUS_QUICK_FILTER_KEYS)[number])) {
          STATUS_QUICK_FILTER_KEYS.forEach((k) => newSet.delete(k));
        }
        newSet.add(filterKey);
      } else {
        newSet.delete(filterKey);
      }
      return newSet;
    });

    // Special handling for unread filter
    if (filterKey === 'unread') {
      setIsRead(active ? false : undefined);
    }
  };

  const onBulkToggleSelect = (id: number, add: boolean) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (add) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const onBulkSelectPage = useCallback((ids: number[], select: boolean) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (select) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const onBulkSetSelection = useCallback((ids: Iterable<number>) => {
    setBulkSelectedIds(new Set(ids));
  }, []);

  const onBulkClearSelection = useCallback(() => {
    setBulkSelectedIds(new Set());
  }, []);

  const runBulkAction = async (
    action: 'mark-read' | 'mark-unread' | 'star' | 'archive' | 'unarchive',
    ids: number[]
  ) => {
    if (!ids.length) return;
    const labels =
      action === 'mark-read'
        ? { title: 'Mark as read', statusLine: 'Marking emails as read…' }
        : action === 'mark-unread'
          ? { title: 'Mark as unread', statusLine: 'Marking emails as unread…' }
          : action === 'star'
            ? { title: 'Star emails', statusLine: 'Starring emails…' }
            : action === 'archive'
              ? { title: 'Archive emails', statusLine: 'Archiving emails…' }
              : { title: 'Unarchive emails', statusLine: 'Unarchiving emails…' };
    setBulkProgress({ ...labels, done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        await api.post(`/emails/${ids[i]}/${action}`);
        setBulkProgress({ ...labels, done: i + 1, total: ids.length });
      }
      listMutateRef.current?.();
      revalidateSidebarCounts();
      mutateSelectedEmail();
      setBulkSelectedIds(new Set());
      const msg =
        action === 'mark-read'
          ? 'Marked as read'
          : action === 'mark-unread'
            ? 'Marked as unread'
            : action === 'star'
              ? 'Starred'
              : action === 'archive'
                ? 'Archived'
                : 'Unarchived';
      showSuccess(`${msg} ${ids.length} email(s)`);
    } catch (err: any) {
      showError(err?.response?.data?.message || `Failed to ${action}`);
    } finally {
      setBulkProgress(null);
    }
  };

  const handleBulkCopyToFolder = async (folderId: number) => {
    const ids = Array.from(bulkSelectedIds);
    if (!ids.length) return;
    setBulkCopyFolderOpen(false);
    const isMove = selectedPersonalFolderId != null && folderId !== selectedPersonalFolderId;
    const labels = isMove
      ? { title: 'Move to folder', statusLine: 'Moving emails…' }
      : { title: 'Add to folder', statusLine: 'Adding emails to folder…' };
    setBulkProgress({ ...labels, done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        await addEmailToFolder(folderId, id);
        if (isMove && selectedPersonalFolderId != null) {
          await removeEmailFromFolder(selectedPersonalFolderId, id);
        }
        setBulkProgress({ ...labels, done: i + 1, total: ids.length });
      }
      listMutateRef.current?.();
      revalidateSidebarCounts();
      setBulkSelectedIds(new Set());
      showSuccess(isMove ? `Moved ${ids.length} email(s) to folder` : `Added ${ids.length} email(s) to folder`);
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to add to folder');
    } finally {
      setBulkProgress(null);
    }
  };

  const handleBulkRemoveFromFolder = async () => {
    if (selectedPersonalFolderId == null) return;
    const ids = Array.from(bulkSelectedIds);
    if (!ids.length) return;
    const labels = { title: 'Remove from folder', statusLine: 'Removing emails from folder…' };
    setBulkProgress({ ...labels, done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        await removeEmailFromFolder(selectedPersonalFolderId, ids[i]);
        setBulkProgress({ ...labels, done: i + 1, total: ids.length });
      }
      listMutateRef.current?.();
      revalidateSidebarCounts();
      setBulkSelectedIds(new Set());
      showSuccess(`Removed ${ids.length} email(s) from folder`);
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to remove from folder');
    } finally {
      setBulkProgress(null);
    }
  };

  const handleBulkAddTag = async (tagId: number) => {
    const ids = Array.from(bulkSelectedIds);
    if (!ids.length) return;
    setBulkTagDropdownOpen(false);
    const labels = { title: 'Add tag', statusLine: 'Adding tag to emails…' };
    setBulkProgress({ ...labels, done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        await api.post(`/emails/${ids[i]}/tags/${tagId}`);
        setBulkProgress({ ...labels, done: i + 1, total: ids.length });
      }
      listMutateRef.current?.();
      mutateSelectedEmail();
      setBulkSelectedIds(new Set());
      showSuccess(`Tag added to ${ids.length} email(s)`);
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to add tag');
    } finally {
      setBulkProgress(null);
    }
  };

  // Handle splitter drag
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const container = document.querySelector('.email-split-container');
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      
      // Constrain between 20% and 80%
      const constrainedPosition = Math.max(20, Math.min(80, newPosition));
      setSplitterPosition(constrainedPosition);
    };

    const handleMouseUp = () => {
      if (isResizing) {
        updateEmailUiPrefs({ splitterPosition: splitterPositionRef.current });
        setIsResizing(false);
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, updateEmailUiPrefs]);

  // Sidebar splitter: persist width on drag end
  const handleSidebarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingSidebar(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingSidebar || !emailsLayoutRef.current) return;
      const rect = emailsLayoutRef.current.getBoundingClientRect();
      const newWidth = e.clientX - rect.left;
      setSidebarWidth((w) => Math.max(180, Math.min(480, newWidth)));
    };

    const handleMouseUp = () => {
      if (isResizingSidebar) {
        updateEmailUiPrefs({ sidebarWidth: sidebarWidthRef.current });
        setIsResizingSidebar(false);
      }
    };

    if (isResizingSidebar) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingSidebar, updateEmailUiPrefs]);

  // Sync is run via cron; no toolbar sync button
  const configWithSync = {
    ...moduleConfig,
    toolbar: {
      ...moduleConfig.toolbar,
      customButton: undefined,
    },
  };

  // Empty state only when there is nothing to show: no shared mail sources and no personal folders/tags to organize with
  const hasSharedMailboxSources =
    sharedAccounts.length > 0 || sharedSearchFolders.length > 0 || sharedFolders.length > 0;
  const hasPersonalOrganization =
    (personalFolders?.length ?? 0) > 0 || (emailTags?.length ?? 0) > 0;
  if (
    !accountsLoading &&
    !sharedAccountsLoading &&
    !sharedSearchFoldersLoading &&
    accountsSharedWithUser.length === 0 &&
    !hasSharedMailboxSources &&
    !hasPersonalOrganization
  ) {
    return (
        <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">📧</div>
          <h2 className={cn('text-2xl font-bold mb-2', colors.text)}>Nothing shared with you yet</h2>
          <p className={cn('mb-6', colors.textSecondary)}>
            When someone shares an email account, smart folder, or personal folder with you, it will appear here.
            Ask a teammate to share access via Email Accounts or Smart Folders.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={emailsLayoutRef} className="flex flex-1 h-full min-h-0 emails-module-layout">
      {/* Left: resizable sidebar (accounts + folders); column shrinks when sidebar is collapsed */}
      <div
        className={cn(
          'flex flex-col h-full min-h-0 flex-shrink-0 overflow-hidden transition-[width] duration-200 min-w-0',
          emailUiPrefs.sidebarCollapsed && 'max-w-[var(--collapsed-rail-width)]'
        )}
        style={{
          ['--collapsed-rail-width' as string]: `${COLLAPSED_RAIL_WIDTH_PX}px`,
          width: emailUiPrefs.sidebarCollapsed ? COLLAPSED_RAIL_WIDTH_PX : sidebarWidth,
          minWidth: emailUiPrefs.sidebarCollapsed ? COLLAPSED_RAIL_WIDTH_PX : sidebarWidth,
          maxWidth: emailUiPrefs.sidebarCollapsed ? COLLAPSED_RAIL_WIDTH_PX : sidebarWidth,
        }}
      >
        <EmailSidebar
          accounts={accountsSharedWithUser}
          sidebarCollapsed={emailUiPrefs.sidebarCollapsed}
          onCollapsedChange={handleSidebarCollapsedChange}
          displayPrefs={emailUiPrefs.displayPrefs}
          onDisplayPrefsChange={handleDisplayPrefsChange}
          personalFolders={personalFolders}
          sharedFolders={sharedFolders}
          unreadCounts={mergedUnreadCounts}
          selectedAccount={selectedAccount}
          selectedPersonalFolderId={selectedPersonalFolderId}
          selectedShareId={selectedShareId}
          onSelectInbox={handleSelectInbox}
          onSelectPersonalFolder={handleSelectPersonalFolder}
          onSelectSharedFolder={handleSelectSharedFolder}
          onCreatePersonalFolder={handleOpenCreateFolderModal}
          onShareFolder={handleShareFolder}
          onRenamePersonalFolder={async (folderId, name) => {
            try {
              await updatePersonalFolder(folderId, { name });
              showSuccess('Folder renamed');
            } catch (err: any) {
              showError(err?.response?.data?.message || 'Failed to rename folder');
            }
          }}
          onMovePersonalFolder={async (folderId, parentId) => {
            try {
              await updatePersonalFolder(folderId, { parentId });
              showSuccess('Folder moved');
            } catch (err: any) {
              showError(err?.response?.data?.message || 'Failed to move folder');
            }
          }}
          onAddSubfolder={(folder) => {
            setCreateFolderParentId(folder.id);
            setCreateFolderModalOpen(true);
          }}
          onDeletePersonalFolder={async (folderId) => {
            try {
              await deletePersonalFolder(folderId);
              if (selectedPersonalFolderId === folderId) setSelectedPersonalFolderId(undefined);
              showSuccess('Folder deleted');
            } catch (err: any) {
              showError(err?.response?.data?.message || 'Failed to delete folder');
            }
          }}
          onDropOnFolder={async (folderId, emailId, partitionAt) => {
            try {
              await addEmailToFolder(folderId, emailId, partitionAt);
              showSuccess('Email added to folder');
            } catch (err: any) {
              showError(err?.response?.data?.message || 'Failed to add email to folder');
            }
          }}
          onReorderPersonalFolders={async (folderIds) => {
            try {
              await reorderPersonalFolders(folderIds);
              showSuccess('Folders reordered');
            } catch (err: any) {
              showError(err?.response?.data?.message || 'Failed to reorder folders');
            }
          }}
          sharedSearchFolders={sharedSearchFolders}
          sharedAccounts={[]}
          selectedSharedSearchFolderShareId={selectedSharedSearchFolder?.shareId}
          selectedSharedAccountShareId={selectedSharedAccount?.shareId}
          selectedSharedAccountFolder={selectedSharedAccountFolder}
          onSelectSharedSearchFolder={handleSelectSharedSearchFolder}
          onSelectSharedAccount={handleSelectSharedAccount}
          emailTags={emailTags}
          selectedTagId={selectedTagId}
          onSelectTag={(tagId) => {
            setMentionsViewActive(false);
            setSelectedTagId(tagId);
            if (tagId != null) {
              setSelectedPersonalFolderId(undefined);
              setSelectedShareId(undefined);
              setSelectedAccount(undefined);
              setSelectedSharedSearchFolder(null);
              setSelectedSharedAccount(null);
              setSelectedSpecialView(null);
              setSelectedImapFolderPath('INBOX');
              setSearchTerm('');
              setSearchInFields(defaultEmailSearchInSet());
            }
          }}
          onCreateTag={createEmailTag}
          onDeleteTag={deleteEmailTag}
          onReorderTags={reorderEmailTags}
          onShareTag={handleShareTag}
          onAccountSidebarColorChange={updateAccountSidebarColor}
          selectedSpecialView={selectedSpecialView}
          onSelectSpecialView={handleSelectSpecialView}
          mentionsViewActive={mentionsViewActive}
          mentionUnreadCount={mentionUnreadCount}
          onSelectMentionsView={handleSelectMentionsView}
          imapFoldersByAccount={imapFoldersByAccount}
          selectedImapFolderPath={selectedAccount != null ? selectedImapFolderPath : 'INBOX'}
          onSelectImapFolder={handleSelectImapFolder}
          onRefresh={async () => {
            // Revalidate all email list and unread-count caches so count + paged list stay in sync
            await globalMutate(
              (key) =>
                (typeof key === 'string' && key.startsWith('/emails')) ||
                (Array.isArray(key) && key[0] === 'imap-folders'),
              undefined,
              { revalidate: true }
            );
            mutateAccounts();
            mutateSharedSearchFolders();
            listMutateRef.current?.();
            revalidateSidebarCounts();
            showSuccess('List refreshed');
          }}
          composeReturnTo={listComposeReturn}
          markFolderAsReadLabel={markFolderAsReadTarget?.folderName ?? null}
          onMarkFolderAsRead={() => setMarkFolderAsReadOpen(true)}
        />
      </div>

      {/* Splitter between sidebar and content (hidden when sidebar collapsed) */}
      {!emailUiPrefs.sidebarCollapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          className={cn(
            'relative flex-shrink-0 w-1 cursor-col-resize transition-colors',
            isResizingSidebar ? colors.primary : colors.surfaceHover,
            'hover:bg-indigo-500'
          )}
          onMouseDown={handleSidebarMouseDown}
        >
          <div className="absolute inset-y-0 -left-1.5 -right-1.5 w-6" aria-hidden />
        </div>
      )}

      {/* Right: search bar + list | detail */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <div className="email-module-filters">
        <EmailFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showYearFilter={dateScopeAppliesToEmailList}
          selectedYear={selectedYear}
          onYearChange={(y) => {
            setSelectedYear(y);
            setPartitionPreset(null);
            if (y != null) persistEmailDateScopeToStorage({ kind: 'year', y });
            else persistEmailDateScopeToStorage({ kind: 'any' });
          }}
          partitionPreset={partitionPreset}
          onPartitionPresetChange={(p) => {
            if (p != null) {
              setPartitionPreset(p);
              setSelectedYear(null);
              persistEmailDateScopeToStorage({ kind: 'preset', p });
              return;
            }
            setPartitionPreset(null);
          }}
          onQuickFilter={handleQuickFilter}
          activeQuickFilters={activeQuickFilters}
          searchInFields={searchInFields}
          onSearchInFieldsChange={setSearchInFields}
          resultCount={selectedSpecialView === 'templates' ? templateCount : (emailsListResult.total ?? emailsData.length)}
          searchFolderName={activeSearchFolder?.name}
          searchFolderPeriodLabel={
            activeSearchFolder
              ? searchFolderPeriodShortLabel(activeSearchFolder.periodPreset, activeSearchFolder.periodYear)
              : null
          }
          showListLayoutToggle={!isSpecialListView && !mentionsViewActive}
          threadedView={emailUiPrefs.threadedView && !mentionsViewActive}
          onThreadedViewChange={(value) => updateEmailUiPrefs({ threadedView: value })}
          showSortControl={!isSpecialListView && !mentionsViewActive}
          listSortBy={emailUiPrefs.listSortBy}
          onListSortByChange={(sortBy) => updateEmailUiPrefs({ listSortBy: sortBy })}
          hideArchivedQuickFilter={selectedSpecialView === 'archived'}
        />
        {selectedSpecialView === 'mySent' && (
          <div
            className={cn(
              'px-3 py-2 flex flex-wrap items-center gap-2 border-b',
              colors.border,
              colors.surface,
            )}
          >
            <span className={cn('text-sm font-semibold', colors.text)}>My sent</span>
            <span className={cn('text-xs', colors.textMuted)}>
              Messages you sent through this app, across all accounts you can use.
            </span>
          </div>
        )}
        {selectedSpecialView === 'archived' && (
          <div
            className={cn(
              'px-3 py-2 flex flex-wrap items-center gap-2 border-b',
              colors.border,
              colors.surface,
            )}
          >
            <span className={cn('text-sm font-semibold', colors.text)}>Archived</span>
            <span className={cn('text-xs', colors.textMuted)}>
              Messages you archived, across all shared mailboxes you can access.
            </span>
          </div>
        )}
        {mentionsViewActive && (
          <div
            className={cn(
              'px-3 py-2 flex flex-wrap items-center gap-2 border-b',
              colors.border,
              colors.surface,
            )}
          >
            <span className={cn('text-sm font-semibold', colors.text)}>Mentions</span>
            <button
              type="button"
              onClick={() =>
                handleQuickFilter('mentionUnreadOnly', !activeQuickFilters.has('mentionUnreadOnly'))
              }
              className={cn(
                'text-xs px-2.5 py-1 rounded-full border transition-colors',
                activeQuickFilters.has('mentionUnreadOnly')
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : cn(colors.border, colors.textSecondary, colors.interactiveHover),
              )}
            >
              Unread mentions
            </button>
            {mentionUnreadCount > 0 && (
              <button
                type="button"
                onClick={() => void handleMarkAllMentionsRead()}
                className="text-xs px-2.5 py-1 rounded-full border border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
              >
                Mark all as read
              </button>
            )}
          </div>
        )}
        {!isSpecialListView && (
          <ActiveFilterChips
            colors={colors}
            partitionPreset={partitionPreset}
            selectedYear={selectedYear}
            dateFilterApplies={dateScopeAppliesToEmailList}
            searchTerm={searchTerm}
            selectedTagId={selectedTagId}
            tagName={emailTags?.find((t) => t.id === selectedTagId)?.name}
            personalFolderName={
              selectedPersonalFolderId != null
                ? flattenPersonalFolders(personalFolders ?? []).find((f) => f.id === selectedPersonalFolderId)?.name
                : undefined
            }
            sharedFolderName={
              selectedShareId != null
                ? flattenSharedFolderNodes(sharedFolders ?? []).find((f) => f.shareId === selectedShareId)?.name
                : undefined
            }
            searchFolderPeriodLabel={activeSearchFolderPeriodChip}
            activeQuickFilters={activeQuickFilters}
            onClearDate={() => {
              setSelectedYear(null);
              setPartitionPreset(null);
              persistEmailDateScopeToStorage({ kind: 'any' });
            }}
            onClearSearch={() => setSearchTerm('')}
            onClearTag={() => setSelectedTagId(undefined)}
            onClearPersonalFolder={() => setSelectedPersonalFolderId(undefined)}
            onClearSharedFolder={() => setSelectedShareId(undefined)}
            onClearQuickFilter={handleQuickFilter}
          />
        )}
        </div>
        {!isOnline && (
          <div className={cn('flex-shrink-0 px-4 py-2 text-sm text-amber-800 bg-amber-100 dark:text-amber-200 dark:bg-amber-900/40 border-b', colors.border)} role="status" aria-live="polite">
            You appear to be offline. Some actions may not work until you're back online.
          </div>
        )}
        <div className="flex-1 min-h-0 flex h-full email-split-container relative">
          {/* Center Panel - Email List, Drafts, or Library */}
          <div 
            className={cn(
              'min-w-0 h-full min-h-0 overflow-hidden border-r flex flex-col',
              colors.border,
              isSpecialListView && 'border-r-0',
              isNarrowEmailLayout && !showEmailListPane && 'hidden',
            )}
            style={{
              width: isSpecialListView
                ? '100%'
                : isNarrowEmailLayout
                  ? '100%'
                  : `${splitterPosition}%`,
            }}
          >
            {selectedSpecialView === 'templates' ? (
              <TemplatesLibraryPanel
                company={companyLibrary}
                personal={personalLibrary}
                shared={sharedLibrary}
                isLoading={templateLibraryLoading}
                composeReturnTo={listComposeReturn}
                onImported={async () => {
                  await mutateTemplateLibrary();
                }}
                onSuccess={showSuccess}
                onError={showError}
                onShareTemplateFolder={(f) => setShareTemplateFolderOpen({ folderId: f.id, folderName: f.name })}
              />
            ) : selectedSpecialView === 'drafts' ? (
              <DraftsList composeReturnTo={listComposeReturn} />
            ) : selectedSpecialView === 'outbox' ? (
              <OutboxList composeReturnTo={listComposeReturn} />
            ) : (
            <div className="flex-1 min-h-0 flex flex-col min-w-0">
            {bulkSelectedIds.size > 0 && (
              <div className={cn('flex-shrink-0 flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 border-b', colors.headerBg, colors.border)}>
                <div className="flex items-center gap-3 min-w-0 flex-1 flex-wrap">
                  <span className={cn('text-sm font-semibold shrink-0', colors.textSecondary)}>
                    {bulkSelectedIds.size} selected
                  </span>
                  <div className={cn('h-5 w-px shrink-0 border-l hidden sm:block', colors.border)} aria-hidden />
                  <div className="flex items-center gap-0.5 flex-wrap overflow-x-auto max-w-full" role="toolbar" aria-label="Bulk actions">
                    <button
                      type="button"
                      disabled={bulkActionLoading}
                      onClick={() => runBulkAction('star', Array.from(bulkSelectedIds))}
                      className={cn('h-9 w-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover)}
                      title="Star"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                    </button>
                    <span className={cn('w-px h-5 mx-0.5 shrink-0 border-l hidden sm:block', colors.border)} aria-hidden />
                    <button
                      type="button"
                      disabled={bulkActionLoading}
                      onClick={() => runBulkAction('mark-read', Array.from(bulkSelectedIds))}
                      className={cn('h-9 w-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover)}
                      title="Mark as read"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </button>
                    <button
                      type="button"
                      disabled={bulkActionLoading}
                      onClick={() => runBulkAction('mark-unread', Array.from(bulkSelectedIds))}
                      className={cn('h-9 w-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover)}
                      title="Mark as unread"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </button>
                    <span className={cn('w-px h-5 mx-0.5 shrink-0 border-l hidden sm:block', colors.border)} aria-hidden />
                    <button
                      type="button"
                      disabled={bulkActionLoading}
                      onClick={() => runBulkAction('archive', Array.from(bulkSelectedIds))}
                      className={cn('h-9 w-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover)}
                      title="Archive"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                    </button>
                    <button
                      type="button"
                      disabled={bulkActionLoading}
                      onClick={() => runBulkAction('unarchive', Array.from(bulkSelectedIds))}
                      className={cn('h-9 w-9 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover)}
                      title="Unarchive"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
                    </button>
                    <span className={cn('w-px h-5 mx-0.5 shrink-0 border-l hidden sm:block', colors.border)} aria-hidden />
                    {emailTags.length > 0 && (
                      <div className="relative inline-block">
                        <button
                          ref={bulkTagBtnRef}
                          type="button"
                          disabled={bulkActionLoading}
                          onClick={() => setBulkTagDropdownOpen((v) => !v)}
                          className={cn('h-9 min-w-[2.25rem] pl-2 pr-1.5 rounded-lg flex items-center justify-center gap-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover, bulkTagDropdownOpen && colors.surfaceHover)}
                          title="Add tag"
                          aria-expanded={bulkTagDropdownOpen}
                          aria-haspopup="menu"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                          <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                        <AnchoredMenu
                          open={bulkTagDropdownOpen}
                          onClose={() => setBulkTagDropdownOpen(false)}
                          anchorRef={bulkTagBtnRef}
                          align="start"
                          className={cn('min-w-[160px] max-h-[200px] overflow-y-auto', colors.surface, colors.border)}
                        >
                          {emailTags.filter((t) => t.isOwner !== false || t.permission === 'write').map((t) => (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => handleBulkAddTag(t.id)}
                              className={cn('w-full text-left px-3 py-2 text-sm flex items-center gap-2', colors.text, colors.interactiveHover)}
                            >
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={t.color ? { backgroundColor: t.color } : { backgroundColor: '#94a3b8' }}
                                aria-hidden
                              />
                              {t.name}
                            </button>
                          ))}
                        </AnchoredMenu>
                      </div>
                    )}
                    {personalFolders.length > 0 && (
                      <>
                        {emailTags.length > 0 && <span className={cn('w-px h-5 mx-0.5 shrink-0 border-l hidden sm:block', colors.border)} aria-hidden />}
                        <div className="relative inline-block">
                          <button
                            ref={bulkCopyFolderBtnRef}
                            type="button"
                            disabled={bulkActionLoading}
                            onClick={() => setBulkCopyFolderOpen((v) => !v)}
                            className={cn('h-9 min-w-[2.25rem] pl-2 pr-1.5 rounded-lg flex items-center justify-center gap-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover, bulkCopyFolderOpen && colors.surfaceHover)}
                            title={selectedPersonalFolderId != null ? 'Move to folder' : 'Add to folder'}
                            aria-expanded={bulkCopyFolderOpen}
                            aria-haspopup="menu"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                            <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          </button>
                          <AnchoredMenu
                            open={bulkCopyFolderOpen}
                            onClose={() => setBulkCopyFolderOpen(false)}
                            anchorRef={bulkCopyFolderBtnRef}
                            align="start"
                            className={cn('min-w-[180px] max-h-[200px] overflow-y-auto', colors.surface, colors.border)}
                          >
                            {flattenPersonalFolders(personalFolders).map((f) => (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => handleBulkCopyToFolder(f.id)}
                                className={cn('w-full text-left px-3 py-2 text-sm flex items-center gap-2', colors.text, colors.interactiveHover)}
                                style={{ paddingLeft: 12 + f.depth * 16 }}
                              >
                                <svg
                                  className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                                  />
                                </svg>
                                {f.name}
                              </button>
                            ))}
                          </AnchoredMenu>
                        </div>
                      </>
                    )}
                    {selectedPersonalFolderId != null && (
                      <>
                        <span className={cn('w-px h-5 mx-0.5 shrink-0 border-l hidden sm:block', colors.border)} aria-hidden />
                        <button
                          type="button"
                          disabled={bulkActionLoading}
                          onClick={handleBulkRemoveFromFolder}
                          className={cn('h-9 px-2.5 sm:px-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed', colors.textMuted, colors.interactiveHover)}
                          title="Remove from folder"
                          aria-label="Remove from folder"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                          <span className="text-sm font-medium hidden sm:inline">Remove from folder</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setBulkSelectedIds(new Set())}
                  className={cn('h-9 px-2.5 rounded-lg flex items-center justify-center gap-1.5 shrink-0 transition-colors', colors.textMuted, colors.interactiveHover)}
                  title="Clear selection"
                  aria-label="Clear selection"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-sm font-medium hidden sm:inline">Clear</span>
                </button>
              </div>
            )}
            <EmailCardList
              useEmailsForList={useEmailsForList}
              setListMutateRef={(mutate) => { listMutateRef.current = mutate; }}
              onEmailClick={(email) => {
                setSelectedEmailId(email.id);
                setSelectedPartitionAt(email.partitionAt ?? null);
              }}
              onListScopeQueryChange={setEmailListScopeQuery}
              onListPaginationSnapshot={(snapshot) => {
                listPaginationSnapshotRef.current = snapshot;
              }}
              initialListPagination={initialListState.restoreListPagination}
              onToggleRead={handleToggleRead}
              selectedEmailId={selectedEmailId}
              configWithSync={configWithSync}
              bulkSelectedIds={bulkSelectedIds}
              onBulkToggleSelect={onBulkToggleSelect}
              onBulkSelectPage={onBulkSelectPage}
              onBulkSetSelection={onBulkSetSelection}
              onBulkClearSelection={onBulkClearSelection}
              onBulkProgress={setBulkProgress}
              bulkOperationBusy={bulkActionLoading}
              showMailboxLocation={showMailboxLocation}
              emptyStateHint={listEmptyHint}
              showReadByCount
              listSortBy={emailUiPrefs.listSortBy}
            />
            </div>
            )}
          </div>

          {/* Resizable Splitter + detail — hidden for drafts/library and narrow single-pane */}
          {!isSpecialListView && showEmailDetailPane && (
          <>
          {!isNarrowEmailLayout && (
          <div
            className={cn(
              'absolute top-0 bottom-0 w-1 cursor-col-resize transition-colors z-10 touch-none',
              colors.surfaceHover,
              'hover:bg-indigo-500',
              isResizing && colors.primary
            )}
            // Sit on the detail side of the boundary so the list scrollbar stays clickable.
            style={{ left: `${splitterPosition}%` }}
            onMouseDown={handleMouseDown}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize email list"
          >
            <div className="absolute inset-y-0 left-0 w-4" aria-hidden />
          </div>
          )}

          {/* Right Panel - Email Detail */}
          <div 
            className={cn('min-w-0 h-full min-h-0 overflow-hidden border-l flex flex-col', colors.border, isNarrowEmailLayout && 'w-full border-l-0')}
            style={{ width: isNarrowEmailLayout ? '100%' : `${100 - splitterPosition}%` }}
          >
            <EmailDetailView
              email={selectedEmail ?? null}
              composeReturnTo={listComposeReturn}
              openCollaborationOnMount={pendingCollaborationOpen}
              onCollaborationOpened={() => setPendingCollaborationOpen(false)}
              threadedView={emailUiPrefs.threadedView}
              listScopeQuery={emailUiPrefs.threadedView && emailListScopeQuery ? emailListScopeQuery : null}
              showAiAssist={enableAiEmails}
              onOpenAiAssist={() => {
                aiReadSelectionRef.current = window.getSelection()?.toString().trim() ?? '';
                setEmailAiDrawerOpen(true);
              }}
              onBack={isNarrowEmailLayout ? handleCloseDetail : undefined}
              onSelectThreadEmail={
                emailUiPrefs.threadedView
                  ? (message) => {
                      setSelectedEmailId(message.id);
                      setSelectedPartitionAt(message.partitionAt ?? null);
                    }
                  : undefined
              }
              onEmailUpdate={(updated) => {
                mutateSelectedEmail();
                if (updated?.isArchived && selectedEmailId === updated.id) {
                  setSelectedEmailId(null);
                  setSelectedPartitionAt(null);
                }
                if (updated && listMutateRef.current) {
                  const listMailboxFolder: AccountMailboxFolder | null =
                    selectedSharedAccount != null
                      ? selectedSharedAccountFolder
                      : selectedAccount != null
                        ? imapPathToMailboxFolder(selectedImapFolderPath)
                        : null;
                  const folderMovedOffList =
                    updated.folder != null &&
                    listMailboxFolder != null &&
                    updated.folder !== listMailboxFolder;

                  if (folderMovedOffList && selectedEmailId === updated.id) {
                    setSelectedEmailId(null);
                    setSelectedPartitionAt(null);
                  }

                  listMutateRef.current(
                    (prev: EmailsResponse | undefined): EmailsResponse | undefined => {
                      if (!prev?.data) return prev;
                      const rows = prev.data as Array<Email | EmailThreadGroup>;
                      if (updated.isArchived || folderMovedOffList) {
                        return {
                          ...prev,
                          data: rows.filter((item) =>
                            'id' in item ? item.id !== updated.id : item.latestEmail.id !== updated.id,
                          ) as EmailsResponse['data'],
                          total: Math.max(0, (prev.total ?? prev.data.length) - 1),
                        };
                      }
                      return {
                        ...prev,
                        data: rows.map((item) => {
                          if ('id' in item && item.id === updated.id) {
                            return {
                              ...item,
                              isRead: updated.isRead,
                              isStarred: updated.isStarred,
                              isArchived: updated.isArchived,
                              hasNote: updated.hasNote,
                              tagIds: updated.tagIds,
                              tags: updated.tags,
                              assignedToUserId: updated.assignedToUserId,
                              assignedToUser: updated.assignedToUser,
                              workflowStatus: updated.workflowStatus,
                              folder: updated.folder ?? item.folder,
                            };
                          }
                          if ('latestEmail' in item && item.latestEmail.id === updated.id) {
                            return {
                              ...item,
                              latestEmail: {
                                ...item.latestEmail,
                                isRead: updated.isRead,
                                isStarred: updated.isStarred,
                                isArchived: updated.isArchived,
                                hasNote: updated.hasNote,
                                tagIds: updated.tagIds,
                                tags: updated.tags,
                                assignedToUserId: updated.assignedToUserId,
                                assignedToUser: updated.assignedToUser,
                                workflowStatus: updated.workflowStatus,
                                folder: updated.folder ?? item.latestEmail.folder,
                              },
                            };
                          }
                          return item;
                        }) as EmailsResponse['data'],
                      };
                    },
                    false
                  );
                }
                if (listMutateRef.current) listMutateRef.current();
                revalidateSidebarCounts();
              }}
              personalFolders={personalFolders}
              emailTags={emailTags}
              onCopyToFolder={async (email, folderId) => {
                try {
                  await addEmailToFolder(folderId, email.id, email.partitionAt);
                  showSuccess('Email added to folder');
                } catch (err: any) {
                  showError(err?.response?.data?.message || 'Failed to add email to folder');
                }
              }}
            />
          </div>
          </>
          )}
        </div>
      </div>

      {shortcutsHelpOpen && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={() => setShortcutsHelpOpen(false)} />
          <div className={cn('fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm rounded-lg border shadow-lg p-4', colors.surface, colors.border)}>
            <h3 className={cn('font-semibold mb-3', colors.text)}>Keyboard shortcuts</h3>
            <ul className={cn('text-sm space-y-1.5', colors.textSecondary)}>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">R</kbd> Reply</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">F</kbd> Forward</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">E</kbd> Edit as new</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">[</kbd><kbd className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">]</kbd> Previous / next in conversation (thread view)</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">J</kbd> Next email</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">K</kbd> Previous email</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">↑</kbd><kbd className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">↓</kbd> Move selection</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">Esc</kbd> Back to list (mobile / close detail)</li>
              <li><kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">?</kbd> Show this help</li>
            </ul>
            <p className={cn('text-xs mt-3', colors.textMuted)}>Press ? to close</p>
          </div>
        </>
      )}

      <ShareFolderModal
        isOpen={shareFolderOpen != null}
        onClose={() => setShareFolderOpen(null)}
        folderId={shareFolderOpen?.folderId ?? null}
        folderName={shareFolderOpen?.folderName ?? ''}
        onShared={() => {}}
        shareFolder={shareFolder}
      />
      <ShareTagModal
        isOpen={shareTagOpen != null}
        onClose={() => setShareTagOpen(null)}
        tagId={shareTagOpen?.tagId ?? null}
        tagName={shareTagOpen?.tagName ?? ''}
        tagColor={shareTagOpen?.tagColor}
        onShared={() => void mutateEmailTags()}
      />
      {shareTemplateFolderOpen != null && (
        <ShareTemplateFolderModal
          isOpen
          onClose={() => setShareTemplateFolderOpen(null)}
          folderId={shareTemplateFolderOpen.folderId}
          folderName={shareTemplateFolderOpen.folderName}
          onShared={() => void mutateTemplateLibrary()}
        />
      )}
      <CreatePersonalFolderModal
        isOpen={createFolderModalOpen}
        onClose={() => setCreateFolderModalOpen(false)}
        initialParentId={createFolderParentId}
        personalFolders={personalFolders}
        onCreate={handleCreatePersonalFolder}
      />

      <EmailBulkProgressModal progress={bulkProgress} />

      <MarkFolderAsReadModal
        isOpen={markFolderAsReadOpen}
        target={markFolderAsReadTarget}
        onClose={() => {
          setMarkFolderAsReadOpen(false);
          listMutateRef.current?.();
          revalidateSidebarCounts();
          void mutateMentionUnreadCount();
        }}
        onComplete={({ marked, cancelled, total }) => {
          if (cancelled) {
            showSuccess(
              marked > 0
                ? `Marked ${marked.toLocaleString()} of ${Math.max(total, marked).toLocaleString()} as read (cancelled)`
                : 'Cancelled',
            );
          } else if (marked === 0) {
            showSuccess('Nothing to mark as read');
          } else {
            showSuccess(`Marked ${marked.toLocaleString()} email(s) as read`);
          }
        }}
      />

      <EmailAiAssistant
        open={emailAiDrawerOpen}
        onClose={() => setEmailAiDrawerOpen(false)}
        context="read"
        presentation="overlay"
        subject={selectedEmail?.subject ?? ''}
        body={emailBodyForAi(selectedEmail)}
        getBody={() => emailBodyForAi(selectedEmail)}
        getSelectedText={() => aiReadSelectionRef.current}
        emailId={selectedEmail?.id}
        partitionAt={selectedEmail?.partitionAt}
        clientId={selectedEmail?.linkedClient?.clientId}
        onCreateReply={(text) => {
          if (!selectedEmailId) return;
          stashComposeInitialUserBody(text);
          openComposeInNewTab(
            emailComposeThreadPath(selectedEmailId, 'reply', selectedEmail?.partitionAt),
          );
          setEmailAiDrawerOpen(false);
        }}
      />
    </div>
  );
};

// Drafts view component (uses EmailDraft records, not IMAP DRAFT folder)
const DraftsList: React.FC<{ composeReturnTo: ComposeReturnTarget }> = ({ composeReturnTo }) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { applicationSettings } = useSettings();
  const showAccountDrafts = applicationSettings?.emailShowAccountDrafts === true;
  const { data: drafts, isLoading, isError, mutate } = useDraftsList();
  const [draftToDelete, setDraftToDelete] = useState<EmailDraftListItem | null>(null);
  const [deletingDraft, setDeletingDraft] = useState(false);

  const handleDeleteDraftConfirm = async () => {
    if (!draftToDelete || draftToDelete.isOwner === false) return;
    setDeletingDraft(true);
    try {
      await deleteDraftApi(draftToDelete.id);
      await revalidateEmailDraftCaches(draftToDelete.id);
      await mutate();
      showSuccess('Draft deleted');
      setDraftToDelete(null);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to delete draft';
      showError(message);
    } finally {
      setDeletingDraft(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className={colors.textMuted}>Loading drafts...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className={colors.textMuted}>Failed to load drafts.</span>
      </div>
    );
  }

  if (!drafts.length) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className={cn('text-center', colors.textMuted)}>
          <p className="text-lg mb-1">No drafts</p>
          <p className="text-sm">
            {showAccountDrafts
              ? 'Drafts from accounts you can access will appear here.'
              : 'Drafts saved from Compose will appear here.'}
          </p>
        </div>
      </div>
    );
  }

  const formatRecipients = (addrs: any): string => {
    if (!addrs) return '';
    const arr = Array.isArray(addrs) ? addrs : [addrs];
    return arr
      .map((a) => {
        if (!a) return '';
        if (typeof a === 'string') return a;
        const email = a.email ?? '';
        const name = a.name ?? '';
        if (name && email) return `${name} <${email}>`;
        return email || name || '';
      })
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
        {drafts.map((draft: EmailDraftListItem) => {
          const accountLabel =
            draft.emailAccount?.displayName ||
            draft.emailAccount?.emailAddress ||
            `Account #${draft.emailAccountId}`;
          const toDisplay = formatRecipients(draft.to);
          const updated = draft.updatedAt ? new Date(draft.updatedAt) : null;
          const isOwnDraft = draft.isOwner !== false;
          const authorLabel =
            showAccountDrafts && draft.ownerName
              ? isOwnDraft
                ? 'You'
                : draft.ownerName
              : null;

          return (
            <li key={draft.id} className="group flex items-stretch gap-1">
            <button
              type="button"
              onClick={() =>
                navigateToCompose(
                  navigate,
                  '/emails/compose',
                  {
                    draft,
                    draftId: draft.id,
                    draftAccountId: draft.emailAccountId,
                    draftAsCopy: !isOwnDraft,
                  },
                  composeReturnTo,
                )
              }
              className={cn(
                'flex-1 min-w-0 text-left rounded-lg border px-3 py-2.5 transition-colors',
                colors.surface,
                colors.border,
                colors.interactiveHover
              )}
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Draft · {accountLabel}
                  {authorLabel ? ` · ${authorLabel}` : ''}
                </span>
                {updated && (
                  <span className={cn('text-xs', colors.textMuted)}>
                    {formatDateTime(updated)}
                  </span>
                )}
              </div>
              <div className={cn('text-sm font-medium truncate', colors.text)}>
                {draft.subject || '(no subject)'}
              </div>
              {toDisplay && (
                <div className={cn('text-xs truncate mt-0.5', colors.textMuted)}>
                  To: {toDisplay}
                </div>
              )}
              {!isOwnDraft && (
                <div className={cn('text-[11px] mt-1', colors.textMuted)}>
                  Opens as a copy in your compose session
                </div>
              )}
            </button>
            {isOwnDraft ? (
              <button
                type="button"
                onClick={() => setDraftToDelete(draft)}
                className={cn(
                  'flex-shrink-0 self-center rounded-lg border p-2 transition-colors opacity-70 group-hover:opacity-100',
                  colors.border,
                  'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20',
                )}
                title="Delete draft"
                aria-label={`Delete draft ${draft.subject || '(no subject)'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : (
              <span className="w-10 flex-shrink-0" aria-hidden />
            )}
            </li>
          );
        })}
        </ul>
      </div>

      <ConfirmationDialog
        isOpen={draftToDelete != null}
        onClose={() => !deletingDraft && setDraftToDelete(null)}
        onConfirm={handleDeleteDraftConfirm}
        title="Delete draft"
        message={
          draftToDelete
            ? `Delete "${draftToDelete.subject || '(no subject)'}"? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deletingDraft}
      />
    </div>
  );
};

// Card View Component
interface EmailCardListProps {
  useEmailsForList: (searchParams?: string, options?: EmailsListOptions) => EmailsListHookResult;
  setListMutateRef: (mutate: EmailsListMutate | null) => void;
  onEmailClick: (email: Email) => void;
  onListScopeQueryChange?: (query: string) => void;
  onListPaginationSnapshot?: (snapshot: EmailListPaginationSnapshot) => void;
  initialListPagination?: EmailListPaginationSnapshot | null;
  onToggleRead?: (email: Email) => void;
  selectedEmailId: number | null;
  configWithSync: any;
  bulkSelectedIds: Set<number>;
  onBulkToggleSelect: (id: number, add: boolean) => void;
  onBulkSelectPage: (ids: number[], select: boolean) => void;
  onBulkSetSelection: (ids: Iterable<number>) => void;
  onBulkClearSelection: () => void;
  onBulkProgress: (progress: EmailBulkProgressState | null) => void;
  bulkOperationBusy?: boolean;
  showMailboxLocation?: boolean;
  emptyStateHint?: string;
  showReadByCount?: boolean;
  listSortBy?: EmailListSortBy;
}

const EmailCardList: React.FC<EmailCardListProps> = ({
  useEmailsForList,
  setListMutateRef,
  onEmailClick,
  onListScopeQueryChange,
  onListPaginationSnapshot,
  initialListPagination,
  onToggleRead,
  selectedEmailId,
  configWithSync,
  bulkSelectedIds,
  onBulkToggleSelect,
  onBulkSelectPage,
  onBulkSetSelection,
  onBulkClearSelection,
  onBulkProgress,
  bulkOperationBusy = false,
  showMailboxLocation = false,
  emptyStateHint,
  showReadByCount = false,
  listSortBy = 'date',
}) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [currentPage, setCurrentPage] = useState(
    () => initialListPagination?.currentPage ?? 1,
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    () =>
      initialListPagination?.itemsPerPage ?? configWithSync.pagination.initialItemsPerPage,
  );
  const [cursorByPage, setCursorByPage] = useState<Record<number, string>>(
    () => initialListPagination?.cursorByPage ?? {},
  );
  const listContainerRef = React.useRef<HTMLDivElement>(null);
  const selectAllCheckboxRef = React.useRef<HTMLInputElement>(null);
  const selectIndexOnNextLoadRef = React.useRef<'first' | 'last' | null>(null);
  const prevScopeRef = React.useRef<string | undefined>(undefined);
  const prevSelectionScopeRef = React.useRef<string | undefined>(undefined);
  const restorePageAttemptedRef = React.useRef(false);

  const pageCursor = currentPage > 1 ? cursorByPage[currentPage] : undefined;
  const listOptions: EmailsListOptions = {
    take: itemsPerPage,
    includeTotal: true,
    ...(pageCursor ? { cursor: pageCursor } : { skip: (currentPage - 1) * itemsPerPage }),
  };

  const { data: emails, total, isLoading, isValidating, loadTimeMs, mutate, requestQuery, nextCursor } =
    useEmailsForList(undefined, listOptions);
  const emailList = emails || [];
  const listScopeKey = requestQuery ? emailListScopeKey(requestQuery) : '';
  const pageEmailIds = useMemo(() => emailList.map((e) => e.id), [emailList]);
  const allPageSelected =
    pageEmailIds.length > 0 && pageEmailIds.every((id) => bulkSelectedIds.has(id));
  const somePageSelected = pageEmailIds.some((id) => bulkSelectedIds.has(id));
  const totalItems = total || 0;
  const allFolderSelected = totalItems > 0 && bulkSelectedIds.size >= totalItems;
  const canSelectAllInFolder = totalItems > pageEmailIds.length && totalItems <= BULK_SELECT_MAX;

  useEffect(() => {
    onListPaginationSnapshot?.({ currentPage, itemsPerPage, cursorByPage });
  }, [currentPage, itemsPerPage, cursorByPage, onListPaginationSnapshot]);

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) el.indeterminate = somePageSelected && !allPageSelected;
  }, [somePageSelected, allPageSelected]);

  useEffect(() => {
    if (prevSelectionScopeRef.current !== undefined && prevSelectionScopeRef.current !== listScopeKey) {
      onBulkClearSelection();
    }
    prevSelectionScopeRef.current = listScopeKey;
  }, [listScopeKey, onBulkClearSelection]);

  const toggleSelectAllOnPage = () => {
    onBulkSelectPage(pageEmailIds, !allPageSelected);
  };

  const selectAllInFolder = useCallback(async () => {
    if (!requestQuery || totalItems === 0) return;
    if (totalItems > BULK_SELECT_MAX) {
      showError(
        `You can select at most ${BULK_SELECT_MAX.toLocaleString()} emails. Use filters to narrow the list (${totalItems.toLocaleString()} match).`,
      );
      return;
    }
    onBulkProgress({ title: 'Select all', statusLine: 'Loading emails…', done: 0, total: totalItems });
    try {
      const ids = new Set<number>();
      const baseParams = new URLSearchParams(requestQuery);
      baseParams.delete('skip');
      baseParams.delete('cursor');
      baseParams.set('take', String(BULK_SELECT_FETCH_SIZE));
      baseParams.set('includeTotal', 'true');

      let cursor: string | null = null;
      for (;;) {
        const params = new URLSearchParams(baseParams);
        if (cursor) {
          params.set('cursor', cursor);
        }
        const res = await api.get<EmailsResponse>(`/emails?${params.toString()}`);
        const batch = (res.data.data ?? []) as Email[];
        batch.forEach((email) => ids.add(email.id));
        onBulkProgress({
          title: 'Select all',
          statusLine: 'Loading emails…',
          done: ids.size,
          total: totalItems,
        });
        if (!res.data.hasMore || !res.data.nextCursor || batch.length === 0) break;
        cursor = res.data.nextCursor;
      }
      onBulkSetSelection(ids);
      showSuccess(`Selected ${ids.size.toLocaleString()} email(s).`);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to load emails for selection');
    } finally {
      onBulkProgress(null);
    }
  }, [requestQuery, totalItems, onBulkSetSelection, onBulkProgress, showSuccess, showError]);

  useEffect(() => {
    if (nextCursor) {
      setCursorByPage((prev) => ({ ...prev, [currentPage + 1]: nextCursor }));
    }
  }, [nextCursor, currentPage]);

  useEffect(() => {
    setCursorByPage({});
    setCurrentPage(1);
  }, [itemsPerPage]);

  useEffect(() => {
    if (prevScopeRef.current !== undefined && prevScopeRef.current !== listScopeKey) {
      setCursorByPage({});
      setCurrentPage(1);
    }
    prevScopeRef.current = listScopeKey;
  }, [listScopeKey]);

  // Keep the selected row on screen after returning from compose/detail.
  useEffect(() => {
    if (isLoading || selectedEmailId == null || emailList.length === 0) return;
    if (emailList.some((email) => email.id === selectedEmailId)) return;
    if (restorePageAttemptedRef.current) return;
    if (!initialListPagination || initialListPagination.currentPage <= 1) return;
    restorePageAttemptedRef.current = true;
    setCurrentPage(initialListPagination.currentPage);
    setCursorByPage(initialListPagination.cursorByPage ?? {});
  }, [isLoading, selectedEmailId, emailList, initialListPagination]);

  useEffect(() => {
    if (requestQuery && onListScopeQueryChange) {
      onListScopeQueryChange(listScopeQueryFromEmailsRequest(requestQuery));
    }
  }, [requestQuery, onListScopeQueryChange]);

  // Expose this list's mutate so parent can optimistically update when detail marks as read
  useEffect(() => {
    setListMutateRef(mutate);
    return () => setListMutateRef(null);
  }, [mutate, setListMutateRef]);

  // After page change, select first or last item when emails load
  useEffect(() => {
    const pending = selectIndexOnNextLoadRef.current;
    if (pending === null || emailList.length === 0) return;
    selectIndexOnNextLoadRef.current = null;
    if (pending === 'first') onEmailClick(emailList[0]);
    else onEmailClick(emailList[emailList.length - 1]);
  }, [emailList, onEmailClick]);

  // Keyboard: Arrow Up/Down, Page Up/Down
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const showFolderLoadTime = !isLoading && !isValidating && loadTimeMs != null;
  const folderLoadTimeLabel = showFolderLoadTime
    ? `Loaded in ${formatFolderLoadTime(loadTimeMs)}`
    : null;
  const handleListKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select') || target.isContentEditable) return;
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (emailList.length === 0) return;
        const idx = selectedEmailId == null ? -1 : emailList.findIndex((em) => em.id === selectedEmailId);
        if (idx < emailList.length - 1) onEmailClick(emailList[idx + 1]);
        else if (idx === -1) onEmailClick(emailList[0]);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (emailList.length === 0) return;
        const idx = selectedEmailId == null ? emailList.length : emailList.findIndex((em) => em.id === selectedEmailId);
        if (idx > 0) onEmailClick(emailList[idx - 1]);
        else if (idx === 0) {
          if (currentPage > 1) {
            setCurrentPage((p) => p - 1);
            selectIndexOnNextLoadRef.current = 'last';
          }
        } else if (idx === -1) onEmailClick(emailList[emailList.length - 1]);
        break;
      }
      case 'PageDown': {
        e.preventDefault();
        if (emailList.length === 0) return;
        if (currentPage < totalPages) {
          setCurrentPage((p) => p + 1);
          selectIndexOnNextLoadRef.current = 'first';
        } else onEmailClick(emailList[emailList.length - 1]);
        break;
      }
      case 'PageUp': {
        e.preventDefault();
        if (emailList.length === 0) return;
        if (currentPage > 1) {
          setCurrentPage((p) => p - 1);
          selectIndexOnNextLoadRef.current = 'last';
        } else onEmailClick(emailList[0]);
        break;
      }
      case 'j':
      case 'J':
        e.preventDefault();
        if (emailList.length === 0) return;
        const idxJ = selectedEmailId == null ? -1 : emailList.findIndex((em) => em.id === selectedEmailId);
        if (idxJ < emailList.length - 1) onEmailClick(emailList[idxJ + 1]);
        else if (idxJ === -1) onEmailClick(emailList[0]);
        break;
      case 'k':
      case 'K':
        e.preventDefault();
        if (emailList.length === 0) return;
        const idxK = selectedEmailId == null ? emailList.length : emailList.findIndex((em) => em.id === selectedEmailId);
        if (idxK > 0) onEmailClick(emailList[idxK - 1]);
        else if (idxK === 0 && currentPage > 1) {
          setCurrentPage((p) => p - 1);
          selectIndexOnNextLoadRef.current = 'last';
        } else if (idxK === -1) onEmailClick(emailList[emailList.length - 1]);
        break;
      default:
        break;
    }
  };

  // Create pagination props that work with backend pagination
  const paginationProps = {
    currentPage,
    itemsPerPage,
    totalItems,
    totalPages,
    onPageChange: setCurrentPage,
    onItemsPerPageChange: (newItemsPerPage: number) => {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    },
  };

  return (
    <div className={cn('flex-1 min-h-0 flex flex-col', colors.background)}>
      {!isLoading && emailList.length > 0 && (
        <>
          <div
            className={cn(
              'flex-shrink-0 flex items-center gap-2 px-3 py-1.5 border-b',
              colors.border,
              colors.headerBg,
            )}
          >
            <input
              ref={selectAllCheckboxRef}
              type="checkbox"
              checked={allPageSelected}
              onChange={toggleSelectAllOnPage}
              disabled={bulkOperationBusy}
              className="rounded"
              title="Select all on this page"
              aria-label="Select all on this page"
            />
            <span className={cn('text-xs', colors.textMuted)}>
              {bulkSelectedIds.size > 0
                ? `${bulkSelectedIds.size.toLocaleString()} selected${allFolderSelected ? ' (all in folder)' : ''}`
                : 'Select all on page'}
            </span>
            {canSelectAllInFolder && (
              <button
                type="button"
                disabled={bulkOperationBusy}
                onClick={() => void selectAllInFolder()}
                className={cn(
                  'ml-auto text-xs underline-offset-2 hover:underline disabled:opacity-50 whitespace-nowrap',
                  colors.primaryFg || colors.text,
                )}
              >
                {bulkOperationBusy
                  ? 'Selecting…'
                  : `Select all ${totalItems.toLocaleString()}`}
              </button>
            )}
          </div>
          {allPageSelected && canSelectAllInFolder && !allFolderSelected && (
            <div
              className={cn(
                'flex-shrink-0 px-3 py-2 text-sm border-b',
                colors.border,
                'bg-indigo-50/90 dark:bg-indigo-950/30',
              )}
            >
              <span className={colors.textSecondary}>
                All {pageEmailIds.length.toLocaleString()} on this page are selected.{' '}
              </span>
              <button
                type="button"
                disabled={bulkOperationBusy}
                onClick={() => void selectAllInFolder()}
                className={cn(
                  'font-medium underline-offset-2 hover:underline disabled:opacity-50',
                  colors.primaryFg || 'text-indigo-700 dark:text-indigo-300',
                )}
              >
                {bulkOperationBusy
                  ? 'Selecting…'
                  : `Select all ${totalItems.toLocaleString()} in this folder`}
              </button>
            </div>
          )}
        </>
      )}
      <div
        ref={listContainerRef}
        tabIndex={0}
        role="listbox"
        aria-label="Email list"
        className="flex-1 overflow-y-auto outline-none focus:ring-0 email-list-scrollbar"
        onKeyDown={handleListKeyDown}
      >
        <EmailCardView
          emails={emailList}
          onEmailClick={onEmailClick}
          onToggleRead={onToggleRead}
          isLoading={isLoading}
          selectedEmailId={selectedEmailId}
          bulkSelectedIds={bulkSelectedIds}
          onBulkToggleSelect={onBulkToggleSelect}
          showMailboxLocation={showMailboxLocation}
          emptyStateHint={emptyStateHint}
          showReadByCount={showReadByCount}
          listSortBy={listSortBy}
        />
      </div>
      {/* Pagination + folder load time */}
      {totalItems > 0 ? (
        <TablePaginationBar
          currentPage={paginationProps.currentPage}
          totalPages={paginationProps.totalPages}
          totalItems={paginationProps.totalItems}
          itemsPerPage={paginationProps.itemsPerPage}
          onPageChange={paginationProps.onPageChange}
          onItemsPerPageChange={paginationProps.onItemsPerPageChange}
          pageSizeOptions={configWithSync.pagination.itemsPerPageOptions}
          pageSizeLabel="Show"
          summaryExtra={
            <>
              {folderLoadTimeLabel && (
                <span className={cn('text-xs tabular-nums whitespace-nowrap', colors.textMuted)}>
                  <span className="mx-1.5 opacity-50">·</span>
                  {folderLoadTimeLabel}
                </span>
              )}
              {canSelectAllInFolder && !allFolderSelected ? (
                <button
                  type="button"
                  disabled={bulkOperationBusy}
                  onClick={() => void selectAllInFolder()}
                  className={cn(
                    'text-xs underline-offset-2 hover:underline disabled:opacity-50 whitespace-nowrap',
                    colors.primaryFg || colors.text,
                  )}
                >
                  {bulkOperationBusy
                    ? 'Selecting…'
                    : `Select all ${totalItems.toLocaleString()}`}
                </button>
              ) : undefined}
            </>
          }
        />
      ) : showFolderLoadTime ? (
        <div
          className={cn(
            'flex shrink-0 items-center border-t px-3 py-1.5 sm:px-4',
            colors.surface,
            colors.border,
          )}
        >
          <span className={cn('text-xs tabular-nums', colors.textMuted)}>{folderLoadTimeLabel}</span>
        </div>
      ) : null}
    </div>
  );
};

export default List;
